import React, {
    FC,
    Key,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import Button from '@material-ui/core/Button'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import { fromEvent, Subscription } from 'rxjs'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import PromptContext, { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import {
    CMEResponse,
    DeviceAdminDTO,
    DeviceResponse,
    DevicesResponse,
    LicenceResponseMapped,
    PaginationInfo,
    SessionAPIResponse
} from '../../../../NonReact/Types/Types'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { AdminFilters, AdminFilterValues, AdminFiltersLocaleText } from '../../Molecules/AdminFilters/AdminFilters'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError, displaySuccessMessage, displayErrorMessage } from '../../../../React/Common/Utils/Messages'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import {
    CXRecordLanguage,
    CXScoreLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    LicenceManagementPageLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    UsersByExtension,
    ReportingLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { IsVarEmpty, isVarNotEmpty, IsStringEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { isEmailAddressValid } from '../../../../React/Common/Utils/ValidityUtils'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ReportingAccessLevels } from '../../../../NonReact/constants/Permissions'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { getLicenceManagementList, getTelephonyServer } from '../../../../React/Common/API/ItemsGetters'
import { getTelSysLicences } from '../../../../React/Common/API/Licence/LicenceAPI'
import { sendWelcomeEmailForUser } from '../../../../React/Common/API/UserAPIUtilities'
import {
    createUser,
    deleteUser,
    getCME,
    getDevice,
    getUser,
    updateDevice,
    updateUser
} from '../../../../NonReact/common/API/EntityAPIClient'
import { useFeatureFlag } from '../../../../React/Common/Hooks/useFeatureFlag'
import {
    CX_SCORE_FEATURE_FLAG,
    CXI_FEATURE_FLAG_KEY_FOR_TELSYS,
    ENABLE_CXI_DATA
} from '../../../../React/Common/Constants/FeatureFlags'
import { telSysCapabilityBitFlags } from '../../../../React/CMEAddModify/Constants'
import { isBitmaskMatching } from '../../../../React/Common/Utils/BitwiseOperations'
import {
    isCxaProductApplicable,
    isCxiProductApplicable,
    isCxrProductApplicable,
    isCxsProductApplicable,
    parseBusinessIntegrationOptions
} from '../../../../React/Common/Utils/CxProductApplicability'
import { AdminUserBody, APIData, JSONResponse } from '../../../../NonReact/common/Types/Types'
import { ApplicationUser } from '../../../../NonReact/common/Types/AddUser/AddUserTypes'
import { getCMEIDFromTelSys } from '../../../../React/Common/API/ItemGetters'
import { getSession } from '../../../../React/Common/API/CommonAPI'
import { EventNames } from '../../../../NonReact/constants/EventNames'
import { userFormStateMapper } from '../../../../React/SelfProvisioning/Mappers/UserMapper'
import { UserDefaults } from '../../../../React/Common/Types'
import { IndividualTelItem, TelSysIntegrationMode } from '../../../../React/Common/Types/Types'
import { UserTypes } from '../../../../React/SelfProvisioning/Constants/Constants'
import { requiresBusinessDeviceScope } from '../../../../React/AddUser/Utils/requiresBusinessDeviceScope'
import userDefaults from '../../../../React/UsersByExtension/Config/UserDefaults.json'
import {
    applicableEnterpriseReportingLevels,
    enterpriseReportingLevelLabel
} from '../../../../React/AddUser/Reporting/ReportingAccessLevels'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { DeviceConfigurationModal } from '../../../../React/DeviceConfiguration/Modal/DeviceConfigurationModal'
import AddUserModal from '../../../../React/AddUser/AddUserModal'
import {
    CxLicenceBreakdownItem,
    CxLicenceSummary,
    LicenceManagementEntryType,
    LicenceManagementEntryType as LicenceManagementEntryTypeValue,
    LicenceManagementListItem,
    LicenceManagementListResponse,
    LicenceManagementListRow,
    LicenceManagementSummary
} from '../../../../NonReact/Types/LicenceManagementPageTypes'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { CXScoreLicences } from '../../../../NonReact/constants/CXScoreLicences'
import {
    BusinessOption,
    INTEGRATION_TYPE,
    IntegrationType
} from '../../../../React/TelSysConfiguration/Constants/Constants'
import { COMMA } from '../../../../NonReact/constants/Separators'
import { CXRecordLicences } from '../../../../NonReact/constants/CXRecordLicences'
import {
    CxiDataEntitlement,
    CxIntegrationLicence
} from '../../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { RESTAPIError, ResultType } from '../../../../NonReact/common/API/REST/RESTAPIError'
import { CxsTelsysMode } from '../../../../NonReact/constants/CxsTelsysModes'
import {
    cxsReplacementForUserType,
    cxsSelectOptionsForAddUser,
    CxScoreLicenceSelectOption,
    ensureCurrentCxsOptionVisible,
    resolveCxsTelsysMode
} from '../../../../NonReact/utils/CxsSubscriptionAccessUtils'
import {
    cxrSelectOptionsForAddUser,
    CxRecordLicenceSelectOption,
    ensureCurrentCxrOptionVisible
} from '../../../../NonReact/utils/CxrSubscriptionAccessUtils'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import {
    HISTORIC_ONLY,
    INTEGRATION_MODE,
    INTEGRATION_MODE_OPTIONS,
    INTEGRATION_TYPE as INTEGRATION_TYPE_FIELD
} from '../../../../NonReact/constants/TelSysConstants'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'

type LicenceSelectOption<TValue extends string = string> = {
    readonly label: string
    readonly value: TValue
}

type LicenceDraftValues = {
    readonly userLicence: string
    readonly cxiLicence: CxIntegrationLicence
    readonly cxiDataEntitlement: boolean
    readonly cxsLicence: CXScoreLicences
    readonly cxrLicence: CXRecordLicences
    readonly cxrRecordEnabled: boolean
    readonly cxsScoreEnabled: boolean
}

/** Current CX monitoring flags on a row's device, fetched when the licences popup opens. */
type DeviceMonitoringFlags = {
    readonly cxsScoreEnabled: boolean
    readonly cxrEnabled: boolean
}

type PendingRowDraft = {
    readonly email?: string
    // True when the operator explicitly entered/confirmed the e-mail (intent to create a user),
    // as opposed to the telephony-username prefill added automatically on load (APD-13333).
    readonly emailStagedByUser?: boolean
    readonly username?: string
    readonly licences?: string
    readonly licenceValues?: LicenceDraftValues
    // Whether to send the welcome e-mail when the staged user is created. Undefined
    // means the operator has not toggled it, which is treated as the default (true).
    readonly welcomeEmail?: boolean
}

/**
 * Default for the welcome-email toggle on a pending row: a welcome e-mail is sent
 * when a new user is created unless the operator clears the checkbox.
 */
const DEFAULT_WELCOME_EMAIL: boolean = true

const DEFAULT_PENDING_LICENCE_VALUES: LicenceDraftValues = {
    userLicence: ReportingAccessLevels.NONE,
    cxiLicence: CxIntegrationLicence.NONE,
    cxiDataEntitlement: false,
    cxsLicence: CXScoreLicences.NONE,
    cxrLicence: CXRecordLicences.NONE,
    cxrRecordEnabled: false,
    cxsScoreEnabled: false
}

const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) return false

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

const EMPTY_SUMMARY: LicenceManagementSummary = {
    TotalDevices: 0,
    TotalUsers: 0,
    AssignedDevices: 0,
    UnassignedDevices: 0
}

const USER_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    NONE: UsersByExtension.UsersByExtension_UserType_NONE,
    None: UsersByExtension.UsersByExtension_UserType_NONE,
    ESSENTIALS: UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
    Essentials: UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
    PROFESSIONAL: UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
    Professional: UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
    ENTERPRISE: UsersByExtension.UsersByExtension_UserType_ENTERPRISE,
    Enterprise: UsersByExtension.UsersByExtension_UserType_ENTERPRISE,
    CALL_CENTRE_AGENT: UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    'Call Centre Agent': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    'Call Center Agent': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    CALL_CENTRE_SUPERVISOR: UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Call Centre Supervisor': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Call Center Supervisor': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Essentials (Extension)': LicenceManagementPageLanguage.LicenceManagementPage_EssentialsExtension
}

const CXS_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CXScoreLicences.NONE]: CXScoreLanguage.CX_Score_Licences_None,
    [CXScoreLicences.CXS_AGENT]: CXScoreLanguage.CX_Score_Licences_CXSAgent,
    [CXScoreLicences.CXS_MANAGER]: CXScoreLanguage.CX_Score_Licences_CXSManager,
    ScoringEnabled: LicenceManagementPageLanguage.LicenceManagementPage_Licence_ScoringEnabled
}

const CXR_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CXRecordLicences.NONE]: CXRecordLanguage.CX_Record_Licences_None,
    [CXRecordLicences.CXR_USER]: CXRecordLanguage.CX_Record_Licences_CXRUser,
    [CXRecordLicences.CXR_MANAGER]: CXRecordLanguage.CX_Record_Licences_CXRManager,
    RecordingEnabled: LicenceManagementPageLanguage.LicenceManagementPage_Licence_RecordingEnabled
}

const CXI_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CxIntegrationLicence.NONE]: UsersByExtension.UsersByExtension_CxiLicenceType_NONE,
    None: UsersByExtension.UsersByExtension_CxiLicenceType_NONE,
    [CxIntegrationLicence.SOLO]: UsersByExtension.UsersByExtension_CxiLicenceType_SOLO,
    Solo: UsersByExtension.UsersByExtension_CxiLicenceType_SOLO,
    [CxIntegrationLicence.TEAMS]: UsersByExtension.UsersByExtension_CxiLicenceType_TEAMS,
    Teams: UsersByExtension.UsersByExtension_CxiLicenceType_TEAMS,
    [CxIntegrationLicence.CXI]: UsersByExtension.UsersByExtension_CxiLicenceType_CX_INTEGRATION,
    'CX Integration': UsersByExtension.UsersByExtension_CxiLicenceType_CX_INTEGRATION
}

/**
 * Resolves a backend licence value to the same display text used by Users by Extension.
 *
 * @param value Raw licence value returned by the licence management API.
 * @param language Loaded language handler.
 * @param languageKeyByValue Map from backend value to the language key used by Users by Extension.
 * @param telSysType Telephony server type used for MST/WxC-specific CXA labels.
 * @returns Localised display label, or the raw value when no shared label exists.
 */
const getSharedLicenceDisplayValue = (
    value: Definable<string>,
    language: CLanguageHandler,
    languageKeyByValue: Readonly<Record<string, string>>,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): string => {
    if (IsVarEmpty(value) || value === EMPTY_STRING) return EMPTY_STRING

    // Teams Business renames the four reporting licences (Historic User / Real-Time User /
    // Agent / Manager) — match the popup's dropdown labels so the column and popup agree.
    if (telSysType === TelSysTypes.MS_TEAMS && integrationType === INTEGRATION_TYPE.BUSINESS) {
        const businessKey: Definable<string> = TEAMS_BUSINESS_USER_LICENCE_LABEL_KEY_BY_VALUE[value]

        if (isVarNotEmpty(businessKey)) return language.get(businessKey)
    }

    const languageKey: Definable<string> = languageKeyByValue[value]

    if (IsVarEmpty(languageKey)) return value

    return language.getWithTelSysCheck(languageKey, telSysType)
}

/**
 * Teams Business display names for the reporting licences, keyed by the stored licence value.
 * Mirrors the popup's option labels (resolveReportingLevelLabel) so both render identically.
 */
const TEAMS_BUSINESS_USER_LICENCE_LABEL_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [ReportingAccessLevels.ESSENTIALS]: ReportingLanguage.AccessLevelHistoricUser,
    [ReportingAccessLevels.PROFESSIONAL]: ReportingLanguage.AccessLevelRealTimeUser,
    [ReportingAccessLevels.CALL_CENTRE_AGENT]: ReportingLanguage.AccessLevelAgent,
    [ReportingAccessLevels.CALL_CENTRE_SUPERVISOR]: ReportingLanguage.AccessLevelManager
}

/**
 * Maps a summary tile's breakdown labels through the shared licence display names.
 *
 * @param data Summary tile data returned by the API.
 * @param language Loaded language handler.
 * @param languageKeyByValue Map from backend values to shared language keys.
 * @param telSysType Telephony server type used for MST/WxC-specific CXA labels.
 * @returns Summary data with display labels normalised for rendering.
 */
const mapCxLicenceSummaryDisplayValues = (
    data: Definable<CxLicenceSummary>,
    language: CLanguageHandler,
    languageKeyByValue: Readonly<Record<string, string>>,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): Definable<CxLicenceSummary> => {
    if (IsVarEmpty(data)) return data

    return {
        ...data,
        Breakdown: data.Breakdown.map(
            (item: CxLicenceBreakdownItem): CxLicenceBreakdownItem => ({
                ...item,
                Label: getSharedLicenceDisplayValue(
                    item.Label,
                    language,
                    languageKeyByValue,
                    telSysType,
                    integrationType
                )
            })
        )
    }
}

const isDisplayableLicenceValue = (value: Definable<string>): boolean =>
    !IsStringEmpty(value) &&
    value !== ReportingAccessLevels.NONE &&
    value !== CXScoreLicences.NONE &&
    value !== CXRecordLicences.NONE &&
    value !== CxIntegrationLicence.NONE

/**
 * Identifies BroadWorks device rows that do not yet have a user, where the UI should show the
 * licence that will be used by default when a user is created.
 *
 * @param item Raw licence management list item.
 * @param entryType Normalised row entry type.
 * @param telSysType Current telephony server type filter.
 * @returns True when the row should display the BroadWorks default user licence.
 */
const shouldDisplayBroadWorksDefaultLicence = (
    item: LicenceManagementListItem,
    entryType: LicenceManagementEntryTypeValue,
    telSysType?: Nullable<number | string>
): boolean =>
    telSysType === TelSysTypes.BROADSOFT_BROADWORKS &&
    entryType === LicenceManagementEntryType.DEVICE &&
    IsStringEmpty(item.Username) &&
    IsStringEmpty(item.UserLicence)

/**
 * Normalises the raw user licence value used by the licence display and draft state.
 *
 * @param item Raw licence management list item.
 * @param entryType Normalised row entry type.
 * @param telSysType Current telephony server type filter.
 * @returns Existing licence, BroadWorks default, or the shared "None" value.
 */
const getUserLicenceValue = (
    item: LicenceManagementListItem,
    entryType: LicenceManagementEntryTypeValue,
    telSysType?: Nullable<number | string>
): string => {
    if (shouldDisplayBroadWorksDefaultLicence(item, entryType, telSysType)) return ReportingAccessLevels.ESSENTIALS

    if (IsStringEmpty(item.UserLicence)) return ReportingAccessLevels.NONE

    return item.UserLicence
}

/** Separator the licences display value is built with; the cell splits on it to stack the lines. */
const LICENCE_DISPLAY_SEPARATOR: string = `${COMMA} `

/**
 * Joins the CXI entitlements (CXI User / CXI Data) into a single licences line. Uses a slash so the
 * combined entry stays on one line — the cell only splits on {@link LICENCE_DISPLAY_SEPARATOR}.
 */
const CXI_ENTITLEMENT_SEPARATOR: string = ' / '

/** Maximum licences shown stacked in a row's Licences cell before collapsing to "[X Licences]". */
const MAX_STACKED_ROW_LICENCES: number = 3

const buildLicenceDisplayValue = (
    licenceValues: LicenceDraftValues,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): string => {
    const userLicence: string = getSharedLicenceDisplayValue(
        licenceValues.userLicence,
        language,
        USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType,
        integrationType
    )
    const cxiLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxiLicence,
        language,
        CXI_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType
    )
    const cxsLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxsLicence,
        language,
        CXS_LICENCE_LANGUAGE_KEY_BY_VALUE
    )
    const cxrLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxrLicence,
        language,
        CXR_LICENCE_LANGUAGE_KEY_BY_VALUE
    )

    return [
        isDisplayableLicenceValue(licenceValues.userLicence) ? userLicence : EMPTY_STRING,
        buildCxiDisplayPart(licenceValues, cxiLicence, language, isCxiDataEnabled),
        buildCxsDisplayPart(licenceValues, cxsLicence, language),
        buildCxrDisplayPart(licenceValues, cxrLicence, language)
    ]
        .filter((s: string): boolean => s !== EMPTY_STRING)
        .join(LICENCE_DISPLAY_SEPARATOR)
}

/**
 * Resolves the CX Integration entry for the licences display, combining both entitlements on one
 * line: "CXI User / CXI Data" when both are held, otherwise whichever single one is held.
 *
 * <p>The generic CXI User entitlement is shown as "CXI User" (matching the popup toggle and the
 * value the backend returns after a save) rather than the legacy "CX Integration" label its stored
 * enum value ({@link CxIntegrationLicence.CXI}) maps to — so toggling CXI User on updates the row
 * immediately, before the save round-trip. Provider subtypes (Solo / Teams) keep their own names.</p>
 */
const buildCxiDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxiUserLabel: string,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean
): string => {
    const cxiUserPart: string = !isDisplayableLicenceValue(licenceValues.cxiLicence)
        ? EMPTY_STRING
        : licenceValues.cxiLicence === CxIntegrationLicence.CXI
        ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiUser)
        : cxiUserLabel
    const cxiDataPart: string =
        licenceValues.cxiDataEntitlement && isCxiDataEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiData)
            : EMPTY_STRING

    return [cxiUserPart, cxiDataPart].filter((s: string): boolean => s !== EMPTY_STRING).join(CXI_ENTITLEMENT_SEPARATOR)
}

/**
 * Resolves the CXS entry for the licences display: "Scoring Enabled" for device-only monitoring
 * (no CXS licence), "CXS Manager + Scoring" when a manager has monitoring on, otherwise the plain
 * licence name.
 */
const buildCxsDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxsLicenceLabel: string,
    language: CLanguageHandler
): string => {
    if (licenceValues.cxsLicence === CXScoreLicences.NONE) {
        return licenceValues.cxsScoreEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Licence_ScoringEnabled)
            : EMPTY_STRING
    }

    if (licenceValues.cxsLicence === CXScoreLicences.CXS_MANAGER && licenceValues.cxsScoreEnabled) {
        return language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_Licence_WithScoring,
            cxsLicenceLabel
        )
    }

    return isDisplayableLicenceValue(licenceValues.cxsLicence) ? cxsLicenceLabel : EMPTY_STRING
}

/**
 * Resolves the CXR entry for the licences display: "Recording Enabled" for device-only monitoring
 * (no CXR licence), "CXR Manager + Recording" when a manager has monitoring on, otherwise the
 * plain licence name.
 */
const buildCxrDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxrLicenceLabel: string,
    language: CLanguageHandler
): string => {
    if (licenceValues.cxrLicence === CXRecordLicences.NONE) {
        return licenceValues.cxrRecordEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Licence_RecordingEnabled)
            : EMPTY_STRING
    }

    if (licenceValues.cxrLicence === CXRecordLicences.CXR_MANAGER && licenceValues.cxrRecordEnabled) {
        return language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_Licence_WithRecording,
            cxrLicenceLabel
        )
    }

    return isDisplayableLicenceValue(licenceValues.cxrLicence) ? cxrLicenceLabel : EMPTY_STRING
}

const getRowLicenceValues = (row: LicenceManagementListRow): LicenceDraftValues => ({
    userLicence: row.UserLicenceValue,
    cxiLicence: row.CXILicenceValue as CxIntegrationLicence,
    cxiDataEntitlement: row.CXIDataEnabled ?? false,
    cxsLicence: row.CXSLicenceValue as CXScoreLicences,
    cxrLicence: row.CXRLicenceValue as CXRecordLicences,
    cxrRecordEnabled: row.CXRecordEnabled ?? false,
    cxsScoreEnabled: row.CXScoreEnabled ?? false
})

/**
 * Normalises the telsys IntegrationType to lower case. The stored value's casing varies across
 * provisioning paths ('Legacy' vs 'legacy'), and a case mismatch against {@link INTEGRATION_TYPE}
 * made the licences popup hide every section on Legacy Teams telsys.
 */
const normaliseTelSysIntegrationType = (telSys: Nullable<IndividualTelItem>): Nullable<IndividualTelItem> =>
    telSys && isStringNotEmpty(telSys.IntegrationType)
        ? { ...telSys, IntegrationType: telSys.IntegrationType?.toLowerCase() as IntegrationType }
        : telSys

/**
 * Row licence values seeded with the device's current CX monitoring flags. The score flag lives on
 * the device only; the record flag comes from the user when one exists, otherwise from the device
 * (licence-less rows can still have device-level monitoring).
 */
const seedRowLicenceValues = (
    row: LicenceManagementListRow,
    monitoring: Definable<DeviceMonitoringFlags>
): LicenceDraftValues => {
    const base: LicenceDraftValues =
        row.Licences === EMPTY_STRING ? DEFAULT_PENDING_LICENCE_VALUES : getRowLicenceValues(row)

    return {
        ...base,
        cxsScoreEnabled: monitoring?.cxsScoreEnabled ?? row.CXScoreEnabled ?? false,
        cxrRecordEnabled: isStringNotEmpty(row.Username)
            ? base.cxrRecordEnabled
            : monitoring?.cxrEnabled ?? row.CXREnabled ?? false
    }
}

const getUserTypeForLicenceValue = (licenceValue: string): string => {
    if (licenceValue === ReportingAccessLevels.CXA_PREMIUM) return UserTypes.CALL_CENTRE_AGENT

    if (licenceValue === ReportingAccessLevels.CXA_ADVANCED) return UserTypes.CALL_CENTRE_SUPERVISOR

    if (licenceValue === ReportingAccessLevels.CALL_CENTRE_AGENT) return UserTypes.CALL_CENTRE_AGENT

    if (licenceValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR) return UserTypes.CALL_CENTRE_SUPERVISOR

    return (
        Object.values(UserTypes).find((userType: string): boolean => {
            const defaultsForUserType: Definable<{ readonly licenceLevel?: string }> = (
                userDefaults as Record<string, { readonly licenceLevel?: string }>
            )[userType]

            return defaultsForUserType?.licenceLevel === licenceValue
        }) ?? UserTypes.ESSENTIALS
    )
}

/**
 * Determines whether the staged CX Score subscription needs an ACD agent endpoint.
 *
 * <p>CX Score Agent always scores the user's own agent, so the backend requires the user to resolve
 * to an agent (error 11414) whatever the CX Analytics tier is — including CXA None, where the tier
 * itself needs no agent and the shared user mapper therefore emits the device endpoint. The mapper
 * cannot correct this: it treats an agent flag on a non-agent tier as a downgrade and drops it, so
 * the endpoint is overridden at the call site (APD-13455).</p>
 *
 * @param cxsLicence Staged CX Score subscription for the row.
 * @param agentNumber The row's own agent number, if it has one.
 * @returns True when the submitted endpoint must be the row's agent rather than its device.
 */
const requiresAgentEndpointForCxs = (
    cxsLicence: Definable<CXScoreLicences>,
    agentNumber: Definable<string>
    ): boolean => cxsLicence === CXScoreLicences.CXS_AGENT && isStringNotEmpty(agentNumber)

/**
 * Builds the ACD agent endpoint for a row, preserving the telsys/partition of the mapped endpoint.
 *
 * <p>The row's assigned agent is used when it has one. Otherwise the device number is sent as the
 * agent number: a matched agent shares its device's number, which is how the shared user mapper
 * emits the agent endpoint for the agent reporting tiers.</p>
 *
 * @param row Licence management row being submitted.
 * @param mappedEndpoint Endpoint produced by the shared user mapper.
 * @returns The same endpoint with the agent number set and the device number cleared.
 */
const buildAgentEndpointForRow = (
    row: LicenceManagementListRow,
    mappedEndpoint: AdminUserBody['Endpoint']
): AdminUserBody['Endpoint'] => ({
    ...mappedEndpoint,
    DeviceNumber: null,
    AgentNumber: isStringNotEmpty(row.AgentNumber) ? row.AgentNumber : row.DeviceNumber
})

/**
 * Adds the row's device to an endpoint that has none, when monitoring is being turned on.
 *
 * <p>CX Record and CX Score are device-level flags, so a user whose endpoint is an ACD agent alone
 * has nowhere for them to live and the backend rejects the licence. The device behind the agent is
 * added instead of asking the operator to assign one by hand, and the agent is left in place —
 * enabling recording must never cost the user their agent endpoint (APD-13568).</p>
 *
 * <p>Only true when the row already has a real agent to point at. Without this, a row that has never
 * had an agent (a device-only user promoted to a tier that can hold an agent, e.g. Supervisor) had its
 * device number submitted as the agent number instead, an endpoint the backend cannot resolve to any
 * agent, leaving the user with neither a device nor an agent (APD-13764).</p>
 *
 * @param endpoint Endpoint the save would otherwise submit.
 * @param row Licence management row being submitted, carrying the resolved device number.
 * @param licenceValues Staged licence values for the row.
 * @returns The endpoint, with the row's device filled in where one is needed and available.
 */
const withMonitoringDevice = (
    endpoint: AdminUserBody['Endpoint'],
    row: LicenceManagementListRow,
    licenceValues: LicenceDraftValues
): AdminUserBody['Endpoint'] => {
    if (isStringNotEmpty(endpoint.DeviceNumber) || isStringNotEmpty(endpoint.AgentNumber)) {
            // Endpoint already holds something -- only a monitoring toggle may add a device to a bare slot.
            const wantsMonitoring = licenceValues.cxsScoreEnabled || licenceValues.cxrRecordEnabled
            if (!wantsMonitoring || isStringNotEmpty(endpoint.DeviceNumber) || IsStringEmpty(row.DeviceNumber)) return endpoint
            return { ...endpoint, DeviceNumber: row.DeviceNumber }
    }

    // Endpoint is empty outright so its the first time this tier has had one. Default it to whichever of the
    // row's own identifiers exists, device preferred, so it isn't left with nothing (APD-13764).
    if (isStringNotEmpty(row.DeviceNumber)) return { ...endpoint, DeviceNumber: row.DeviceNumber }
    if (isStringNotEmpty(row.AgentNumber)) return { ...endpoint, AgentNumber: row.AgentNumber }
    return endpoint
}

/**
 * Reconciles the endpoint against the reporting tier being saved.
 *
 * <p>The tier dictates what its endpoint may hold: a Historic User reports on a partition, a
 * Real-Time User on its own extension device, and an Agent on its own ACD agent. Carrying the
 * previous tier's endpoint over is rejected by the backend — 11279 for a device the tier cannot
 * hold, 11284 for an agent it cannot hold — and turning monitoring on could add a device to a tier
 * that forbids one. Manager and None accept either a device or an agent, so their endpoint stands
 * as it is (APD-13643).</p>
 *
 * @param userType Reporting tier being saved.
 * @param endpoint Endpoint the save would otherwise submit.
 * @param row Licence management row being submitted, carrying the resolved device number.
 * @param licenceValues Staged licence values for the row.
 * @returns The endpoint reconciled against the tier.
 */
const endpointForTier = (
    userType: string,
    endpoint: AdminUserBody['Endpoint'],
    row: LicenceManagementListRow,
    licenceValues: LicenceDraftValues
): AdminUserBody['Endpoint'] => {
    // A Historic User reports on a partition, so neither a device nor an agent may be its endpoint.
    // Its scope still carries the row's device, so a monitoring licence has somewhere to live.
    if (userType === UserTypes.ESSENTIALS) {
        return { ...endpoint, DeviceNumber: null, AgentNumber: null }
    }

    // A Real-Time User reports on its own extension: the device is required and an agent is not
    // allowed. The row's device stands in when the endpoint carried only an agent, which is what
    // the backend would otherwise have to resolve for us.
    if (userType === UserTypes.PROFESSIONAL) {
        return {
            ...endpoint,
            DeviceNumber: isStringNotEmpty(endpoint.DeviceNumber)
                ? endpoint.DeviceNumber
                : row.DeviceNumber,
            AgentNumber: null
        }
    }

    // An Agent reports on its own ACD agent, a device is not allowed in the saved state, but the
    // backend itself resolves a submitted device into its matching agent when none is already
    // known (CReqRespUserBase.SetIDsForEndpoint), the mirror image of how it resolves a device for
    // an agent-only Real-Time User endpoint. So the row's device is submitted here when no agent is
    // already known, rather than guessed at on the frontend, and the backend either resolves it to
    // the matching agent or rejects the save with its own validation error when the device has none
    // at all. Without this, a device-only tier promoted straight to Agent fell through to the
    // generic rule below, which would have kept the device as the endpoint outright, an endpoint
    // this tier cannot hold (11284) (APD-13764).
    if (userType === UserTypes.CALL_CENTRE_AGENT) {
        return isStringNotEmpty(endpoint.AgentNumber)
            ? { ...endpoint, DeviceNumber: null }
            : {
                  ...endpoint,
                  AgentNumber: null,
                  DeviceNumber: isStringNotEmpty(endpoint.DeviceNumber) ? endpoint.DeviceNumber : row.DeviceNumber
              }
    }

    return withMonitoringDevice(endpoint, row, licenceValues)
}

/**
 * Fields the licences popup owns, resolved from the staged draft.
 *
 * @param licenceValues Staged licence values for the row.
 * @returns The scope fragment carrying only the four licence fields the popup can change.
 */
const licenceScopeFragment = (
    licenceValues: LicenceDraftValues
): Pick<
    AdminUserBody['Scope'],
    | 'CXILicenseType'
    | 'CXIDataEntitlement'
    | 'CXScoreLicenseType'
    | 'CXScoreEnabled'
    | 'CXRLicenseType'
    | 'CXRecordEnabled'
> => ({
    CXILicenseType: licenceValues.cxiLicence.toUpperCase() as CxIntegrationLicence,
    CXIDataEntitlement: licenceValues.cxiDataEntitlement ? CxiDataEntitlement.CXI_DATA : CxiDataEntitlement.NONE,
    CXScoreLicenseType: licenceValues.cxsLicence,
    CXScoreEnabled: licenceValues.cxsScoreEnabled,
    CXRLicenseType: licenceValues.cxrLicence,
    CXRecordEnabled: licenceValues.cxrRecordEnabled
})

/**
 * Builds the update body for a row that already has an application user, starting from the user's
 * stored record.
 *
 * <p>The popup owns four licence fields and nothing else, so everything the operator did not touch —
 * the endpoint, time zone, administrative rights, call-recording integration, report permissions — is
 * carried over verbatim. Rebuilding the body from tier defaults instead silently rewrote the rest of
 * the account: it dropped an ACD agent endpoint, reset the time zone and stripped an administrator's
 * rights on nothing more than a monitoring toggle (APD-13568).</p>
 *
 * @param existing Stored user record the update is applied to.
 * @param licenceValues Staged licence values for the row.
 * @param endpoint Endpoint to submit, when the staged licences require a specific one.
 * @param tierDefaults Tier-derived fields, supplied only when the reporting level changed.
 * @returns The body to submit for the user.
 */
const buildLicenceUpdateBody = (
    existing: ApplicationUser,
    licenceValues: LicenceDraftValues,
    endpoint: Definable<AdminUserBody['Endpoint']>,
    tierDefaults: Definable<AdminUserBody>
): AdminUserBody => ({
    Username: existing.Username,
    EmailAddr: existing.EmailAddr,
    FullName: existing.FullName,
    DefaultLang: existing.DefaultLang,
    TimeZone: existing.TimeZone,
    LockOut: existing.LockOut,
    EnableMFA: existing.EnableMFA,
    SendWelcomeEmail: false,
    Advanced: existing.Advanced,
    Endpoint: endpoint ?? existing.Endpoint,
    Scope: {
        ...existing.Scope,
        ...licenceScopeFragment(licenceValues),
        // The staged reporting level is authoritative: it is seeded from the row, so it matches the
        // stored level when the operator did not change it.
        ReportingAccess: licenceValues.userLicence,
        // A changed reporting level re-derives the fields that level dictates; on an unchanged level
        // the stored values stand.
        ...(tierDefaults
            ? {
                  DeviceNumber: tierDefaults.Scope.DeviceNumber,
                  AgentNumber: tierDefaults.Scope.AgentNumber,
                  EnhancedMobileReporting: tierDefaults.Scope.EnhancedMobileReporting,
                  DefaultReportCollection: tierDefaults.Scope.DefaultReportCollection
              }
            : {})
    },
    // Report permissions follow the reporting level, so they are re-derived with it. Administrative
    // rights never do, and are always the user's own.
    Permissions: {
        ...existing.Permissions,
        NonAdmin: tierDefaults ? tierDefaults.Permissions.NonAdmin : existing.Permissions.NonAdmin
    }
})

/**
 * Gets the username that will be used when the pending row is submitted.
 *
 * @param row Licence management row being staged for user creation.
 * @param email Pending e-mail address entered by the operator.
 * @returns Telephony username when available, otherwise the pending e-mail address.
 */
const getPendingUsername = (row: LicenceManagementListRow, email: string): string =>
    IsStringEmpty(row.TelSysUsername) ? email : row.TelSysUsername

/**
 * Builds the local-only draft rendered before the create-user tick is clicked.
 *
 * @param row Licence management row being staged for user creation.
 * @param email Pending e-mail address entered by the operator.
 * @param language Loaded language handler.
 * @param isCxiDataEnabled Whether the CXI Data feature flag is enabled.
 * @param telSysType Telephony server type used for MST/WxC-specific CXA labels.
 * @returns Draft values to render in the row before server submission.
 */
const getPendingUserDraft = (
    row: LicenceManagementListRow,
    email: string,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): PendingRowDraft => {
    // Seed from the row so any device-level monitoring already set (e.g. Score Enabled) is reflected
    // in the draft's display and values, rather than defaulting to "no licences" and hiding it
    // behind the prefill (APD-13333).
    const seeded: LicenceDraftValues = seedRowLicenceValues(row, undefined)

    return {
        email,
        username: getPendingUsername(row, email),
        licences: buildLicenceDisplayValue(seeded, language, isCxiDataEnabled, telSysType, integrationType),
        licenceValues: seeded
    }
}

/**
 * Telephony server types whose devices carry an e-mail address the page can default a new user to.
 *
 * Mirrors the server-side map {@code CTelephoneSystemConst.s_pTelsysTypeToTelsysEmailFieldMap}: Teams
 * supplies it as the Teams user id and Webex Calling as the device primary e-mail, and both arrive in
 * the row's {@code TelSysUsername}. Other types put a non-e-mail identifier there (BroadWorks uses
 * {@code IDOnTelSys}), so they must not be prefilled.
 */
const EMAIL_PREFILL_TELSYS_TYPES: readonly TelSysTypes[] = [TelSysTypes.MS_TEAMS, TelSysTypes.WEBEX_CALLING]

/**
 * Builds initial pending e-mail drafts for device rows whose telephony username is the e-mail.
 *
 * Follows the same rule as the legacy add-user modal: default the e-mail only when the target is
 * empty and the candidate is a valid address. That modal additionally skipped overwriting a Webex
 * Calling supervisor's username; it cannot apply here, because a prefill only touches rows with no
 * user and no e-mail, and no reporting access level has been chosen at load time.
 *
 * @param rows Rows returned by the licence management list API.
 * @param language Loaded language handler.
 * @param isCxiDataEnabled Whether the CXI Data feature flag is enabled.
 * @param telSysType Current telephony server type filter.
 * @returns Pending drafts keyed by row key for rows that should be prefilled on load.
 */
const buildPrefilledEmailDrafts = (
    rows: readonly LicenceManagementListRow[],
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): ReadonlyMap<Key, PendingRowDraft> => {
    if (!EMAIL_PREFILL_TELSYS_TYPES.includes(telSysType as TelSysTypes)) return new Map()

    return rows.reduce(
        (drafts: Map<Key, PendingRowDraft>, row: LicenceManagementListRow): Map<Key, PendingRowDraft> => {
            if (
                row.EntryType === LicenceManagementEntryType.DEVICE &&
                IsStringEmpty(row.Username) &&
                IsStringEmpty(row.EmailAddr) &&
                isStringNotEmpty(row.TelSysUsername) &&
                isEmailAddressValid(row.TelSysUsername)
            ) {
                drafts.set(
                    row.key,
                    getPendingUserDraft(
                        row,
                        row.TelSysUsername,
                        language,
                        isCxiDataEnabled,
                        telSysType,
                        integrationType
                    )
                )
            }

            return drafts
        },
        new Map<Key, PendingRowDraft>()
    )
}

/**
 * Adds load-time e-mail drafts while preserving operator edits already staged on other rows.
 *
 * @param currentDrafts Existing staged row changes.
 * @param rows Rows returned by the licence management list API.
 * @param language Loaded language handler.
 * @param isCxiDataEnabled Whether the CXI Data feature flag is enabled.
 * @param telSysType Current telephony server type filter.
 * @returns Existing drafts plus any missing Teams e-mail preload drafts.
 */
const mergePrefilledEmailDrafts = (
    currentDrafts: ReadonlyMap<Key, PendingRowDraft>,
    rows: readonly LicenceManagementListRow[],
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): ReadonlyMap<Key, PendingRowDraft> => {
    const prefilledDrafts: ReadonlyMap<Key, PendingRowDraft> = buildPrefilledEmailDrafts(
        rows,
        language,
        isCxiDataEnabled,
        telSysType,
        integrationType
    )

    return Array.from(prefilledDrafts).reduce(
        (drafts: Map<Key, PendingRowDraft>, [key, draft]: [Key, PendingRowDraft]): Map<Key, PendingRowDraft> => {
            if (!drafts.has(key)) {
                drafts.set(key, draft)
            }

            return drafts
        },
        new Map<Key, PendingRowDraft>(currentDrafts)
    )
}

type EmailEditCellProps = {
    readonly row: LicenceManagementListRow
    readonly pendingEmail?: string
    readonly defaultEmail?: string
    readonly addEmailTooltip: string
    readonly onSaved: (newEmail: string) => void
    readonly onLoadingChange: (loading: boolean) => void
    readonly onPendingChange: (email: string | null) => void
}

const EmailEditCell: FC<EmailEditCellProps> = ({
    row,
    pendingEmail,
    defaultEmail,
    addEmailTooltip,
    onSaved,
    onLoadingChange,
    onPendingChange
}: EmailEditCellProps): ReactElement => {
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const displayValue: string = pendingEmail ?? row.EmailAddr
    const [value, setValue] = useState<string>(displayValue)
    const inputRef = useRef<HTMLInputElement>(null)
    const isMounted = useRef<boolean>(true)
    const hasUser: boolean = row.Username !== EMPTY_STRING
    const hasPendingEmail: boolean = !IsStringEmpty(pendingEmail)
    const resolvedDefaultEmail: string = defaultEmail ?? EMPTY_STRING
    const hasDefaultEmail: boolean = isStringNotEmpty(resolvedDefaultEmail)

    useEffect((): (() => void) => {
        isMounted.current = true

        return (): void => {
            isMounted.current = false
        }
    }, [])

    useEffect((): void => {
        if (!isEditing) {
            setValue(displayValue)
        }
    }, [displayValue, isEditing])

    useEffect((): void => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    // Update-only path — only used when the row already has a user.
    const handleSave = useCallback((): void => {
        const trimmed: string = value.trim()

        if (trimmed === EMPTY_STRING || trimmed === row.EmailAddr) {
            setValue(row.EmailAddr)
            setIsEditing(false)
            return
        }

        onLoadingChange(true)
        updateUser(row.Username, { EmailAddr: trimmed } as AdminUserBody)
            .then((): void => {
                onSaved(trimmed)

                if (isMounted.current) {
                    setValue(trimmed)
                    setIsEditing(false)
                }
            })
            .catch((err: Error): void => {
                onLoadingChange(false)
                displayError(err)

                if (isMounted.current) {
                    setValue(row.EmailAddr)
                    setIsEditing(false)
                }
            })
    }, [value, row, onSaved, onLoadingChange])

    const stagePendingEmail = useCallback((): void => {
        const trimmed: string = value.trim()

        if (trimmed !== EMPTY_STRING) {
            setValue(trimmed)
            onPendingChange(trimmed)
        } else {
            setValue(row.EmailAddr)
            onPendingChange(null)
        }

        setIsEditing(false)
    }, [value, row.EmailAddr, onPendingChange])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && hasUser) {
            e.preventDefault()
            handleSave()
        }

        if (e.key === 'Enter' && !hasUser) {
            e.preventDefault()
            stagePendingEmail()
        }

        if (e.key === 'Escape') {
            e.preventDefault()
            setValue(displayValue)
            setIsEditing(false)

            if (!hasUser) onPendingChange(null)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue: string = e.target.value

        setValue(newValue)
    }

    const renderEmailContent = (): ReactElement => {
        if (isEditing) {
            return (
                <input
                    ref={inputRef}
                    type='text'
                    className='lmp-email-edit-input'
                    value={value}
                    onChange={handleChange}
                    onBlur={hasUser ? handleSave : stagePendingEmail}
                    onKeyDown={handleKeyDown}
                />
            )
        }

        if (hasUser || hasPendingEmail) {
            return <span className='lmp-email-display'>{displayValue}</span>
        }

        return (
            <span
                className='lmp-email-display lmp-email-display--empty'
                onClick={(e: React.MouseEvent): void => {
                    e.stopPropagation()
                    if (hasDefaultEmail && IsStringEmpty(value)) {
                        setValue(resolvedDefaultEmail)
                    }
                    setIsEditing(true)
                }}
                title={addEmailTooltip}
            >
                <Icon iconName={IconName.PENCIL} />
            </span>
        )
    }

    return (
        <div className='lmp-email-edit-cell'>
            {renderEmailContent()}
            {!isEditing && (hasUser || hasPendingEmail) && (
                <IconButton
                    type={IconButtonType.BUTTON}
                    iconName={IconName.PENCIL}
                    onClick={(e?: React.MouseEvent): void => {
                        e?.stopPropagation()
                        setIsEditing(true)
                    }}
                />
            )}
        </div>
    )
}

/**
 * Formats a licence usage figure for the summary tiles: "used/cap" when an effective licence
 * cap applies (telsys caps take priority over CME caps, resolved by the backend), otherwise
 * just the used count.
 *
 * @param count Number of licences in use.
 * @param cap Effective licence cap, when one is set.
 * @returns The display string for the tile.
 */
const formatLicenceCount = (count: number, cap: Definable<number>): string =>
    isVarNotEmpty(cap) ? `${count}/${cap}` : `${count}`

type CxLicenceTileProps = {
    readonly iconName: IconName
    readonly modifierClassName: string
    readonly title: string
    readonly licencesLabel: string
    readonly data: CxLicenceSummary | undefined
}

const CxLicenceTile: FC<CxLicenceTileProps> = ({
    iconName,
    modifierClassName,
    title,
    licencesLabel,
    data
}: CxLicenceTileProps): ReactElement => (
    <Paper elevation={0} className={`cx-licence-tile ${modifierClassName}`}>
        <div className='cx-licence-tile__backdrop' aria-hidden='true'>
            <Icon iconName={iconName} />
        </div>
        <Typography variant='body2' className='cx-licence-tile__title'>
            {title}
        </Typography>
        <div className='cx-licence-tile__content'>
            <div className='cx-licence-tile__summary'>
                <Typography variant='h3' className='cx-licence-tile__total'>
                    {formatLicenceCount(data?.Total ?? 0, data?.Cap)}
                </Typography>
                <Typography variant='body2' className='cx-licence-tile__sub-label'>
                    {licencesLabel}
                </Typography>
            </div>
            {data && data.Breakdown.length > 0 && (
                <div className='cx-licence-tile__breakdown'>
                    {data.Breakdown.map(({ Label, Count, Cap }: CxLicenceBreakdownItem, index: number) => (
                        <React.Fragment key={`${Label}-${index}`}>
                            <Typography variant='caption' className='cx-licence-tile__breakdown-label'>
                                {Label}
                            </Typography>
                            <Typography variant='caption' className='cx-licence-tile__breakdown-count'>
                                {formatLicenceCount(Count, Cap)}
                            </Typography>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    </Paper>
)

type LicenceSectionVisibility = {
    readonly cxAnalytics: boolean
    readonly cxScore: boolean
    readonly cxRecord: boolean
    readonly cxIntegration: boolean
}

/**
 * Resolves which licence popup sections apply for the telsys, by integration type / business
 * integration options (APD-12945):
 * - Legacy Teams: only CX Analytics and CX Integration are applicable.
 * - Business Teams: each section requires its matching business integration option.
 * - CX Score is also supported on BroadWorks (or a Simulator emulating BroadWorks, per
 *   {@code callControlOptions}) but never on other telsys such as Webex Calling (APD-13344), so it
 *   is gated on the business options only for MS Teams. CX Record is MS Teams Business only.
 * - A Business Teams telsys in Historic mode carries no business integration options (the telsys
 *   config clears them outside real-time mode), but CX Analytics still applies — Historic User and
 *   Manager remain assignable, matching the Add/Modify User modal — so CX Analytics is offered on
 *   Business Historic regardless of the (absent) CX-Analytics option (APD-13426).
 * - A telsys in CXI-Only integration mode does no CX Analytics reporting, so the CX Analytics
 *   section never applies there (APD-13472).
 *
 * @param integrationMode The telsys integration mode ({@code IntegrationMode}).
 * @param isTelSysHistoricOnly Whether the telsys runs in Historic mode ({@code HistoricOnly}).
 */
const resolveLicenceSectionVisibility = (
    telSysType: Nullable<number | string>,
    integrationType: Nullable<IntegrationType>,
    integrationMode: Nullable<TelSysIntegrationMode>,
    isTelSysHistoricOnly: Nullable<boolean>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>,
    partitionLicences?: Nullable<readonly string[]>,
    callControlOptions?: Nullable<string>,
    cxScoreFeatureEnabled: boolean = true,
    cxIntegrationAvailable: boolean = true
): LicenceSectionVisibility => {
    // CXI API is an API-only telsys: its users cannot hold a reporting / CX Score / CX Record licence,
    // and CXI User does not apply either — the only selectable entitlement is CXI Data. Show just the
    // CX Integration section (which renders the CXI Data toggle) and hide the rest.
    if (telSysType === TelSysTypes.CXI_API) {
        return { cxAnalytics: false, cxScore: false, cxRecord: false, cxIntegration: true }
    }

    return {
        cxAnalytics: isCxaProductApplicable(
            telSysType,
            integrationType,
            integrationMode,
            businessIntegrationOptions,
            isTelSysHistoricOnly
        ),
        // Shared product rules (CxProductApplicability) keep this page aligned with the
        // Add/Modify User modal and Users by Extension; the CX Score feature flag and the
        // CXI feature flag + CME capability apply on top, matching those surfaces.
        cxScore:
            cxScoreFeatureEnabled &&
            isCxsProductApplicable(telSysType, businessIntegrationOptions, partitionLicences, callControlOptions),
        cxRecord: isCxrProductApplicable(businessIntegrationOptions),
        cxIntegration:
            cxIntegrationAvailable && isCxiProductApplicable(telSysType, integrationType, businessIntegrationOptions)
    }
}

/** Whether the popup would offer at least one licence section for the telsys. */
const hasAnyLicenceSection = (visibility: LicenceSectionVisibility): boolean =>
    visibility.cxAnalytics || visibility.cxScore || visibility.cxRecord || visibility.cxIntegration

/** Summary tile lg-breakpoint width per visible tile count, so the row always fills the page. */
const SUMMARY_TILE_WIDTH_BY_COUNT: Readonly<Record<number, 3 | 4 | 6 | 12>> = { 1: 12, 2: 6, 3: 4, 4: 3 }

type LicenceManagementPopupProps = {
    readonly row: LicenceManagementListRow
    readonly anchorEl: Nullable<HTMLElement>
    readonly initialValues: LicenceDraftValues
    readonly language: CLanguageHandler
    readonly telSysType?: Nullable<number | string>
    // Tri-state on purpose: the reporting-level rules distinguish "historic-only", "known not
    // historic-only" and "unknown". HistoricOnly is an MS Teams concept, so for every other telsys
    // it is absent, and collapsing that absence into false wrongly suppresses Historic User /
    // WxC User (APD-13634).
    readonly historicOnly?: Nullable<boolean>
    readonly licenceSummary: Nullable<LicenceResponseMapped>
    readonly businessIntegrationOptions?: readonly BusinessOption[]
    readonly integrationType?: Nullable<IntegrationType>
    readonly integrationMode?: Nullable<TelSysIntegrationMode>
    readonly partitionLicences?: Nullable<readonly string[]>
    readonly callControlOptions?: Nullable<string>
    readonly cxScoreFeatureEnabled: boolean
    readonly cxIntegrationAvailable: boolean
    readonly isCxiDataEnabled: boolean
    readonly onChange: (values: LicenceDraftValues) => void
    readonly onClose: () => void
}

const LicenceManagementPopup: FC<LicenceManagementPopupProps> = ({
    row,
    anchorEl,
    initialValues,
    language,
    telSysType,
    historicOnly,
    licenceSummary,
    businessIntegrationOptions,
    integrationType,
    integrationMode,
    partitionLicences,
    callControlOptions,
    cxScoreFeatureEnabled,
    cxIntegrationAvailable,
    isCxiDataEnabled,
    onChange,
    onClose
}: LicenceManagementPopupProps): ReactElement => {
    const isMsTeams: boolean = telSysType === TelSysTypes.MS_TEAMS
    // CXS options are mapped to the reporting tier for BroadWorks (and a Simulator emulating it) - CXS Agent only
    // for Call Centre Agent, CXS Manager only for Call Centre Supervisor (APD-13382) - and MS Teams Business,
    // matching the backend rule in CReqRespUserBase.isUnsupportedReportingAccessForCXSLicense; the two modes differ
    // only for None reporting.
    const cxsTelsysMode: Definable<CxsTelsysMode> = resolveCxsTelsysMode(
        telSysType,
        callControlOptions,
        isMsTeams && integrationType === INTEGRATION_TYPE.BUSINESS
    )
    // CXI API is API-only: CXI User does not apply, so the CX Integration section shows the CXI Data
    // toggle alone (APD-12501 entitlement split).
    const isApiOnly: boolean = telSysType === TelSysTypes.CXI_API
    // CX monitoring acts on the row's device, and the CXS Agent / CXR User licences require one
    // (the backend rejects them otherwise) — without a device neither is offered (APD-12965).
    const hasMonitoringDevice: boolean = row.HasDevice && isStringNotEmpty(row.DeviceNumber)
    // Agent reporting tiers require an ACD agent endpoint. The backend list sets HasAgent when the row
    // has an assigned agent OR an extension device with a matching agent the backend can derive — so it,
    // not an assigned AgentNumber alone, is the correct gate for whether an agent endpoint is resolvable.
    const hasAgent: boolean = row.HasAgent
    // The row's originally-assigned licences, independent of the live draft, so a tier the row already
    // holds stays selectable while the popup is open and the operator can switch back to it — e.g.
    // Essentials, which the reporting-level rules only keep when it is the "current" level once its
    // pool is exhausted or the telsys is not historic-only (APD-13444). Anchoring the option lists on
    // the draft instead dropped the assigned tier the moment a different one was picked. Monitoring
    // flags do not affect the licence anchors, so the monitoring arg is omitted.
    const originalLicenceValues: LicenceDraftValues = useMemo(
        (): LicenceDraftValues => seedRowLicenceValues(row, undefined),
        [row]
    )
    const sectionVisibility: LicenceSectionVisibility = resolveLicenceSectionVisibility(
        telSysType,
        integrationType,
        integrationMode,
        historicOnly,
        businessIntegrationOptions,
        partitionLicences,
        callControlOptions,
        cxScoreFeatureEnabled,
        cxIntegrationAvailable
    )
    const showCxAnalyticsSection: boolean = sectionVisibility.cxAnalytics
    const showCxScoreSection: boolean = sectionVisibility.cxScore
    const showCxRecordSection: boolean = sectionVisibility.cxRecord
    const showCxIntegrationSection: boolean = sectionVisibility.cxIntegration
    // CX Analytics (user licence) options. Uses the shared reporting-level rules so the popup matches
    // the Add/Modify User modal: MS Teams Business offers Historic User / Real-Time User / Agent /
    // Manager per real-time/historic mode; Legacy/other telsys keep the existing options/labels.
    const userLicenceOptions: readonly LicenceSelectOption[] = useMemo((): readonly LicenceSelectOption[] => {
        const noneOption: LicenceSelectOption = {
            label: language.get(UsersByExtension.UsersByExtension_UserType_NONE),
            value: ReportingAccessLevels.NONE
        }
        const levelOptions: LicenceSelectOption[] = applicableEnterpriseReportingLevels(
            licenceSummary,
            telSysType as Nullable<TelSysTypes>,
            historicOnly,
            integrationType,
            originalLicenceValues.userLicence
        )
            .filter((level: ReportingAccessLevels): boolean => {
                // Never hide the row's originally-assigned level, so it can always be switched back to.
                if (level === originalLicenceValues.userLicence) return true

                // Agent tiers need an ACD agent endpoint. Offer them only when one is resolvable for the
                // row (an assigned agent, or an extension device with a matching agent — both reflected
                // in hasAgent), so the tick can never fail on a device that has no matching agent.
                if (level === ReportingAccessLevels.CALL_CENTRE_AGENT || level === ReportingAccessLevels.CXA_PREMIUM) {
                    return hasAgent
                }

                // Real-time User / Professional needs an extension endpoint; offer it only when a device
                // is available so it can never be ticked without a resolvable extension.
                if (level === ReportingAccessLevels.PROFESSIONAL) {
                    return hasMonitoringDevice
                }

                return true
            })
            .map(
                (level: ReportingAccessLevels): LicenceSelectOption => ({
                    label: enterpriseReportingLevelLabel(
                        language,
                        level,
                        telSysType as Nullable<TelSysTypes>,
                        integrationType
                    ),
                    value: level
                })
            )

        return [noneOption, ...levelOptions]
    }, [
        language,
        telSysType,
        historicOnly,
        integrationType,
        licenceSummary,
        originalLicenceValues.userLicence,
        hasMonitoringDevice,
        hasAgent
    ])
    const cxsLicenceOptions: readonly CxScoreLicenceSelectOption[] = useMemo(
        (): readonly CxScoreLicenceSelectOption[] =>
            ensureCurrentCxsOptionVisible(
                cxsSelectOptionsForAddUser(
                    initialValues.userLicence,
                    (i18nKey: string): string => language.get(i18nKey),
                    cxsTelsysMode
                ).filter(
                    // CXS Agent always scores the user's own agent, so the backend requires the user
                    // to resolve to one (error 11414) — a device alone is not enough. Offer it only
                    // when an agent is resolvable so it can never fail on a missing one (APD-13455).
                    (option: CxScoreLicenceSelectOption): boolean =>
                        hasAgent || option.value !== CXScoreLicences.CXS_AGENT
                ),
                initialValues.cxsLicence,
                (i18nKey: string): string => language.get(i18nKey)
            ),
        [language, initialValues.userLicence, initialValues.cxsLicence, cxsTelsysMode, hasMonitoringDevice, hasAgent]
    )
    const cxrLicenceOptions: readonly CxRecordLicenceSelectOption[] = useMemo(
        (): readonly CxRecordLicenceSelectOption[] =>
            ensureCurrentCxrOptionVisible(
                cxrSelectOptionsForAddUser(initialValues.userLicence, (i18nKey: string): string =>
                    language.get(i18nKey)
                ).filter(
                    (option: CxRecordLicenceSelectOption): boolean =>
                        hasMonitoringDevice || option.value !== CXRecordLicences.CXR_USER
                ),
                initialValues.cxrLicence,
                (i18nKey: string): string => language.get(i18nKey)
            ),
        [language, initialValues.userLicence, initialValues.cxrLicence, hasMonitoringDevice]
    )
    const updateValues = (values: LicenceDraftValues): void => {
        onChange(values)
    }

    // Score monitoring is forced on for CXS Agent (inherently scores all calls); CXS Manager
    // and None leave the device monitoring flag freely toggleable (APD-12965).
    const isCxsScoringForced: boolean = initialValues.cxsLicence === CXScoreLicences.CXS_AGENT

    /** Resolves the forced score-monitoring state implied by a CXS licence, or the current state. */
    const scoreEnabledForCxsLicence = (licence: CXScoreLicences): boolean => {
        switch (licence) {
            case CXScoreLicences.CXS_AGENT:
                return true
            case CXScoreLicences.NONE:
                return false
            default:
                return initialValues.cxsScoreEnabled
        }
    }

    const handleUserLicenceChange = (option: Nullable<SelectFilterOption>): void => {
        if (!option) return

        const newUserLicence: string = String(option.value)
        const newCxsLicence: CXScoreLicences = cxsReplacementForUserType(
            initialValues.cxsLicence,
            newUserLicence,
            cxsTelsysMode
        )

        updateValues({
            ...initialValues,
            userLicence: newUserLicence,
            cxsLicence: newCxsLicence,
            cxsScoreEnabled: scoreEnabledForCxsLicence(newCxsLicence)
        })
    }

    const handleCxsLicenceChange = (option: Nullable<SelectFilterOption>): void => {
        if (!option) return

        const newCxsLicence: CXScoreLicences = option.value as CXScoreLicences

        updateValues({
            ...initialValues,
            cxsLicence: newCxsLicence,
            cxsScoreEnabled: scoreEnabledForCxsLicence(newCxsLicence)
        })
    }

    const toggleCxsScoreEnabled = (): void => {
        updateValues({
            ...initialValues,
            cxsScoreEnabled: !initialValues.cxsScoreEnabled
        })
    }

    const isCxrRecordingForced: boolean = initialValues.cxrLicence === CXRecordLicences.CXR_USER

    const handleCxrLicenceChange = (option: Nullable<SelectFilterOption>): void => {
        if (!option) return

        const newLicence: CXRecordLicences = option.value as CXRecordLicences
        let recordEnabled: boolean

        switch (newLicence) {
            case CXRecordLicences.CXR_USER:
                recordEnabled = true
                break
            case CXRecordLicences.NONE:
                recordEnabled = false
                break
            default:
                recordEnabled = initialValues.cxrRecordEnabled
        }

        updateValues({ ...initialValues, cxrLicence: newLicence, cxrRecordEnabled: recordEnabled })
    }

    const toggleCxrRecordEnabled = (): void => {
        updateValues({
            ...initialValues,
            cxrRecordEnabled: !initialValues.cxrRecordEnabled
        })
    }

    // CXI needs a device or an agent on the row (the backend rejects it otherwise), and enabling
    // it needs an available CX Integration licence; turning it off is always allowed.
    const canToggleCxiLicence: boolean =
        (hasMonitoringDevice || hasAgent) &&
        (initialValues.cxiLicence !== CxIntegrationLicence.NONE ||
            (licenceSummary?.AvailableLicences?.CXIntegration ?? 1) > 0)

    const toggleCxiLicence = (): void => {
        if (!canToggleCxiLicence) return

        updateValues({
            ...initialValues,
            cxiLicence:
                initialValues.cxiLicence === CxIntegrationLicence.NONE
                    ? CxIntegrationLicence.CXI
                    : CxIntegrationLicence.NONE
        })
    }

    // CXI Data is a separate entitlement from CXI User (APD-12501). It does not require a device
    // or agent on the row — the backend auto-creates a companion device for CXI Data users
    // (APD-12502) — so enabling it only needs an available CXI Data licence; disabling is always
    // allowed.
    const canToggleCxiDataEntitlement: boolean =
        isCxiDataEnabled && (initialValues.cxiDataEntitlement || (licenceSummary?.AvailableLicences?.CXIData ?? 1) > 0)

    const toggleCxiDataEntitlement = (): void => {
        if (!canToggleCxiDataEntitlement) return

        updateValues({
            ...initialValues,
            cxiDataEntitlement: !initialValues.cxiDataEntitlement
        })
    }

    const handleClickAway = (event: React.MouseEvent<Document, MouseEvent>): void => {
        if ((event.target as Element).closest('.MuiAutocomplete-popper')) return

        onClose()
    }

    return (
        <Popper
            open={isVarNotEmpty(anchorEl)}
            anchorEl={anchorEl}
            placement='bottom-end'
            className='lmp-licence-popover-popper'
            // In-tree (not portaled) so .licence-management-page-scoped SCSS still matches.
            disablePortal={true}
            modifiers={{
                flip: { enabled: true },
                preventOverflow: { enabled: true, boundariesElement: 'viewport' },
                offset: { offset: '0, 8' }
            }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Paper elevation={12} className='lmp-licence-popover'>
                    <button
                        type='button'
                        className='lmp-licence-popover__close'
                        aria-label={language.get(LicenceManagementPageLanguage.LicenceManagementPage_Popup_Close)}
                        onClick={onClose}
                    >
                        <Icon iconName={IconName.X} />
                    </button>
                    <Typography variant='h5' className='lmp-licence-popover__title'>
                        {language.get(LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Title)}
                    </Typography>
                    <Typography variant='subtitle1' className='lmp-licence-popover__subtitle'>
                        {row.Description}
                    </Typography>

                    {showCxAnalyticsSection && (
                        <section className='lmp-licence-popover__section'>
                            <Typography variant='body1' className='lmp-licence-popover__section-title'>
                                {language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Analytics)}
                            </Typography>
                            <div className='lmp-licence-popover__select'>
                                <SelectFilter
                                    label={language.get(
                                        LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Licence
                                    )}
                                    icon={<Icon iconName={IconName.LICENCES} />}
                                    options={userLicenceOptions as SelectFilterOption[]}
                                    value={
                                        userLicenceOptions.find(
                                            (opt: LicenceSelectOption): boolean =>
                                                opt.value === initialValues.userLicence
                                        ) ?? null
                                    }
                                    onChange={handleUserLicenceChange}
                                />
                            </div>
                        </section>
                    )}

                    {showCxScoreSection && (
                        <section className='lmp-licence-popover__section'>
                            <Typography variant='body1' className='lmp-licence-popover__section-title'>
                                {language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Score)}
                            </Typography>
                            <button
                                type='button'
                                className={`lmp-licence-popover__switch lmp-licence-popover__switch--score ${
                                    initialValues.cxsScoreEnabled ? 'is-on' : ''
                                }`}
                                disabled={isCxsScoringForced || !hasMonitoringDevice}
                                onClick={toggleCxsScoreEnabled}
                            >
                                <span aria-hidden='true' />
                                {language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_ScoreUserCalls
                                )}
                                <strong>
                                    {language.get(
                                        initialValues.cxsScoreEnabled
                                            ? LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_On
                                            : LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Off
                                    )}
                                </strong>
                            </button>
                            {cxsLicenceOptions.length > 1 && (
                                <div className='lmp-licence-popover__select'>
                                    <SelectFilter
                                        label={language.get(
                                            LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Licence
                                        )}
                                        icon={<Icon iconName={IconName.LICENCES} />}
                                        options={cxsLicenceOptions as SelectFilterOption[]}
                                        value={
                                            cxsLicenceOptions.find(
                                                (opt: CxScoreLicenceSelectOption): boolean =>
                                                    opt.value === initialValues.cxsLicence
                                            ) ?? null
                                        }
                                        onChange={handleCxsLicenceChange}
                                    />
                                </div>
                            )}
                        </section>
                    )}
                    {showCxRecordSection && cxrLicenceOptions.length > 1 && (
                        <section className='lmp-licence-popover__section'>
                            <Typography variant='body1' className='lmp-licence-popover__section-title'>
                                {language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Record)}
                            </Typography>
                            <button
                                type='button'
                                className={`lmp-licence-popover__switch lmp-licence-popover__switch--score ${
                                    initialValues.cxrRecordEnabled ? 'is-on' : ''
                                }`}
                                disabled={isCxrRecordingForced || !hasMonitoringDevice}
                                onClick={toggleCxrRecordEnabled}
                            >
                                <span aria-hidden='true' />
                                {language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_RecordUserCalls
                                )}
                                <strong>
                                    {language.get(
                                        initialValues.cxrRecordEnabled
                                            ? LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_On
                                            : LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Off
                                    )}
                                </strong>
                            </button>
                            {cxrLicenceOptions.length > 1 && (
                                <div className='lmp-licence-popover__select'>
                                    <SelectFilter
                                        label={language.get(
                                            LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_Licence
                                        )}
                                        icon={<Icon iconName={IconName.LICENCES} />}
                                        options={cxrLicenceOptions as SelectFilterOption[]}
                                        value={
                                            cxrLicenceOptions.find(
                                                (opt: CxRecordLicenceSelectOption): boolean =>
                                                    opt.value === initialValues.cxrLicence
                                            ) ?? null
                                        }
                                        onChange={handleCxrLicenceChange}
                                    />
                                </div>
                            )}
                        </section>
                    )}
                    {showCxIntegrationSection && (
                        <section className='lmp-licence-popover__section'>
                            <div className='lmp-licence-popover__switch-stack'>
                                {!isApiOnly && (
                                    <button
                                        type='button'
                                        className={`lmp-licence-popover__switch ${
                                            initialValues.cxiLicence !== CxIntegrationLicence.NONE ? 'is-on' : ''
                                        }`}
                                        disabled={!canToggleCxiLicence}
                                        onClick={toggleCxiLicence}
                                    >
                                        <span aria-hidden='true' />
                                        {language.get(
                                            LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiUser
                                        )}
                                    </button>
                                )}
                                {isCxiDataEnabled && (
                                    <button
                                        type='button'
                                        className={`lmp-licence-popover__switch ${
                                            initialValues.cxiDataEntitlement ? 'is-on' : ''
                                        }`}
                                        disabled={!canToggleCxiDataEntitlement}
                                        onClick={toggleCxiDataEntitlement}
                                    >
                                        <span aria-hidden='true' />
                                        {language.get(
                                            LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiData
                                        )}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}
                </Paper>
            </ClickAwayListener>
        </Popper>
    )
}

/**
 * Routed Fast Provisioning licence management page.
 *
 * @returns Licence management page content.
 */

type DeleteUserCellProps = {
    row: LicenceManagementListRow
    onDeleted: () => void
    onLoadingChange: (loading: boolean) => void
}

const DeleteUserCell: FC<DeleteUserCellProps> = ({
    row,
    onDeleted,
    onLoadingChange
}: DeleteUserCellProps): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const [isConfirmDelete, setIsConfirmDelete]: ReactState<boolean> = useState<boolean>(false)
    const [crossPopperOpen, setCrossPopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const [eventSubscription, setEventSubscription]: ReactState<Definable<Subscription>> =
        useState<Definable<Subscription>>(undefined)
    const buttonRef: MutableRefObject<null> = useRef<null>(null)

    const removeUserClickHandler = (): void => {
        if (isConfirmDelete) {
            setIsConfirmDelete(false)
            onLoadingChange(true)
            deleteUser(row.Username)
                .then((): void => {
                    onDeleted()
                })
                .catch((err: Error): void => {
                    onLoadingChange(false)
                    displayError(err)
                })
        } else {
            setIsConfirmDelete(true)
        }
    }

    const handleClickAway = (event: MouseEvent): void => {
        if (event) {
            if (buttonRef.current && !(buttonRef.current as unknown as Node).contains(event.target as Node)) {
                setIsConfirmDelete(false)
            } else {
                removeUserClickHandler()
            }
        }
    }

    useEffect((): void => {
        if (isConfirmDelete) {
            setEventSubscription(
                fromEvent(document, EventNames.MOUSEDOWN).subscribe((event: Event): void =>
                    handleClickAway(event as MouseEvent)
                )
            )
        } else if (eventSubscription) {
            eventSubscription.unsubscribe()
            setEventSubscription(undefined)
        }
    }, [isConfirmDelete])

    return (
        <div className='lmp-cell-action' onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
            <Button
                onMouseOver={(): void => {
                    if (!isConfirmDelete) setCrossPopperOpen(true)
                }}
                onMouseOut={(): void => setCrossPopperOpen(false)}
                className='ubeu-remove ube-entry-action'
                ref={buttonRef}
                onClick={removeUserClickHandler}
            >
                {isConfirmDelete ? <Icon iconName={IconName.DELETE} /> : <Icon iconName={IconName.X} />}
            </Button>
            <Popper
                anchorEl={buttonRef.current}
                open={isConfirmDelete || crossPopperOpen}
                className='ubeu-remove-confirm-popper'
                placement='top'
            >
                <div className='ubeu-remove-confirm-message'>
                    {crossPopperOpen && !isConfirmDelete
                        ? language.get(UsersByExtension.UsersByExtensions_UserFields_Cross_Tooltip)
                        : language.get(UsersByExtension.UsersByExtensions_ConfirmUserDelete)}
                </div>
            </Popper>
        </div>
    )
}

type RestoreUserCellProps = {
    row: LicenceManagementListRow
    onRestored: () => void
    onLoadingChange: (loading: boolean) => void
}

/**
 * <p>Action cell rendered for a soft-deleted (pending deletion) row: a single restore button that
 * un-deletes the row's user, mirroring the legacy per-extension "Users" modal.</p>
 *
 * <p>Restore issues a minimal {@code { State: 'restore' }} update for the row's username; the
 * backend clones the existing user and only clears the pending-deletion flag, so no other user
 * fields are affected. On success {@link RestoreUserCellProps.onRestored} refreshes the row.</p>
 *
 * @param props Cell props: the row, a restored callback, and a loading-state callback.
 * @returns The restore action cell.
 */
const RestoreUserCell: FC<RestoreUserCellProps> = ({
    row,
    onRestored,
    onLoadingChange
}: RestoreUserCellProps): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const [restorePopperOpen, setRestorePopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const buttonRef: MutableRefObject<null> = useRef<null>(null)

    const restoreUserClickHandler = (): void => {
        onLoadingChange(true)
        // A minimal body is sufficient to restore: the backend re-clones the existing user and the
        // presence of the State property clears the pending-deletion flag without wiping fields.
        updateUser(row.Username, { State: 'restore' } as unknown as AdminUserBody)
            .then((): void => {
                onRestored()
            })
            .catch((err: Error): void => {
                onLoadingChange(false)
                displayError(err)
            })
    }

    return (
        <div className='lmp-cell-action' onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
            <Button
                onMouseOver={(): void => setRestorePopperOpen(true)}
                onMouseOut={(): void => setRestorePopperOpen(false)}
                className='ube-entry-action'
                ref={buttonRef}
                onClick={restoreUserClickHandler}
            >
                <Icon iconName={IconName.USER_RESTORE} />
            </Button>
            <Popper
                anchorEl={buttonRef.current}
                open={restorePopperOpen}
                className='ubeu-remove-confirm-popper'
                placement='top'
            >
                <div className='ubeu-remove-confirm-message'>
                    {language.get(UsersByExtension.UsersByExtensions_UserFields_RestoreBtn_Tooltip)}
                </div>
            </Popper>
        </div>
    )
}

export const LicenceManagementPage: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(LicenceManagementPageLanguage.NAME)
    language.loadBundle(UsersByExtension.NAME)
    language.loadBundle(CXScoreLanguage.NAME)
    language.loadBundle(CXRecordLanguage.NAME)
    language.loadBundle(ReportingLanguage.NAME)

    const [rows, setRows]: ReactState<readonly LicenceManagementListRow[]> = useState<
        readonly LicenceManagementListRow[]
    >([])
    const [rawSummary, setRawSummary]: ReactState<LicenceManagementSummary> =
        useState<LicenceManagementSummary>(EMPTY_SUMMARY)
    const [loadingRowKeys, setLoadingRowKeys]: ReactState<ReadonlySet<Key>> = useState<ReadonlySet<Key>>(new Set())
    const [pendingRowDraftByKey, setPendingRowDraftByKey]: ReactState<ReadonlyMap<Key, PendingRowDraft>> = useState<
        ReadonlyMap<Key, PendingRowDraft>
    >(new Map())
    const [selectedDeviceRow, setSelectedDeviceRow]: ReactState<Nullable<LicenceManagementListRow>> =
        useState<Nullable<LicenceManagementListRow>>(null)
    const [selectedUserRow, setSelectedUserRow]: ReactState<Nullable<LicenceManagementListRow>> =
        useState<Nullable<LicenceManagementListRow>>(null)
    const [selectedLicenceRow, setSelectedLicenceRow]: ReactState<Nullable<LicenceManagementListRow>> =
        useState<Nullable<LicenceManagementListRow>>(null)
    const [licencePopoverAnchor, setLicencePopoverAnchor]: ReactState<Nullable<HTMLElement>> =
        useState<Nullable<HTMLElement>>(null)
    // Current device-level CX monitoring flags per row, fetched lazily when the licences popup
    // opens. Used to seed the popup toggles and to detect monitoring-only changes that are saved
    // straight to the device rather than through user provisioning (APD-12965).
    const [deviceMonitoringByKey, setDeviceMonitoringByKey]: ReactState<ReadonlyMap<Key, DeviceMonitoringFlags>> =
        useState<ReadonlyMap<Key, DeviceMonitoringFlags>>(new Map<Key, DeviceMonitoringFlags>())
    const [currentTelSys, setCurrentTelSys]: ReactState<Nullable<IndividualTelItem>> =
        useState<Nullable<IndividualTelItem>>(null)
    const [licenceSummaryForOptions, setLicenceSummaryForOptions]: ReactState<Nullable<LicenceResponseMapped>> =
        useState<Nullable<LicenceResponseMapped>>(undefined)
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState(DEFAULT_ROWS_PER_PAGE)
    const [filtersReady, setFiltersReady]: ReactState<boolean> = useState<boolean>(false)
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const hasLoadedOnce: MutableRefObject<boolean> = useRef<boolean>(false)

    const listCmeId: string = String(filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID)
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const telSysFilterValue: Definable<string | number> = filters?.telephonyServer?.value
    const partitionFilterValue: Definable<string | number> = filters?.partition?.value
    const listTelSysId: string | number = isConcreteTelSysOrPartitionFilterValue(telSysFilterValue)
        ? (telSysFilterValue as string | number)
        : ALL_VALUE
    const listPartitionId: string | number =
        isConcreteTelSysOrPartitionFilterValue(telSysFilterValue) &&
        isConcreteTelSysOrPartitionFilterValue(partitionFilterValue)
            ? (partitionFilterValue as string | number)
            : ALL_VALUE
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false
    const listTelSysType: Definable<string> = filters?.telephonyServer?.telSysType

    // Business licence integration options enabled on the selected telsys (CX-Score / CX-Record /
    // CX-Integration), used to show/hide the matching CX sections in the licence popup.
    const enabledBusinessOptions: Definable<readonly BusinessOption[]> = useMemo(
        (): Definable<readonly BusinessOption[]> =>
            parseBusinessIntegrationOptions(currentTelSys?.IntegrationModeOptions),
        [currentTelSys]
    )

    // Licence section visibility for the current telsys — drives the popup sections, the summary
    // tiles and whether the Licences cells are clickable. When no section applies (e.g. a Business
    // Teams telsys with no CX integration options enabled), the cells render non-clickable instead
    // of opening an empty popup.
    // Same availability gates as the Add/Modify User modal: the CX Score feature flag, and the
    // per-telsys CXI feature flag combined with the CME's CX Integration capability.
    const isCxScoreFeatureEnabled: boolean = useFeatureFlag(CX_SCORE_FEATURE_FLAG, false)
    const isCxiFeatureEnabled: boolean =
        useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[listTelSysType as TelSysTypes], false) ?? false
    const isCxiDataEnabled: boolean = useFeatureFlag(ENABLE_CXI_DATA, false) ?? false
    const [cmeCapabilities, setCmeCapabilities]: ReactState<Nullable<number>> = useState<Nullable<number>>(undefined)
    const cxIntegrationAvailable: boolean = useMemo((): boolean => {
        if (!isCxiFeatureEnabled) return false

        if (IsVarEmpty(listTelSysType) || !(listTelSysType in telSysCapabilityBitFlags)) return false

        return (
            isVarNotEmpty(cmeCapabilities) &&
            isBitmaskMatching(cmeCapabilities, telSysCapabilityBitFlags[listTelSysType as TelSysTypes])
        )
    }, [isCxiFeatureEnabled, listTelSysType, cmeCapabilities])

    // Telsys integration type drives the Teams Business licence display names — shared by the
    // row mapper, the staged-draft display and the summary tiles.
    // Narrow cast: IntegrationType is normalised to the lowercase IntegrationType union at fetch
    // (normaliseTelSysIntegrationType).
    const listIntegrationType: Nullable<IntegrationType> = currentTelSys?.IntegrationType as Nullable<IntegrationType>

    const licenceSectionVisibility: LicenceSectionVisibility = useMemo(
        (): LicenceSectionVisibility =>
            resolveLicenceSectionVisibility(
                listTelSysType,
                currentTelSys?.IntegrationType,
                currentTelSys?.IntegrationMode,
                currentTelSys?.HistoricOnly,
                enabledBusinessOptions,
                currentTelSys?.PartitionLicences,
                currentTelSys?.CallControlOptions,
                isCxScoreFeatureEnabled,
                cxIntegrationAvailable
            ),
        [listTelSysType, currentTelSys, enabledBusinessOptions, isCxScoreFeatureEnabled, cxIntegrationAvailable]
    )
    const licenceSectionsAvailable: boolean = hasAnyLicenceSection(licenceSectionVisibility)
    const visibleTileCount: number = [
        licenceSectionVisibility.cxAnalytics,
        licenceSectionVisibility.cxScore,
        licenceSectionVisibility.cxRecord,
        licenceSectionVisibility.cxIntegration
    ].filter(Boolean).length
    const summaryTileWidth: 3 | 4 | 6 | 12 = SUMMARY_TILE_WIDTH_BY_COUNT[visibleTileCount] ?? 3

    const setRowLoading = (key: Key, isLoading: boolean): void => {
        setLoadingRowKeys((prev: ReadonlySet<Key>): ReadonlySet<Key> => {
            const next: Set<Key> = new Set(prev)
            if (isLoading) {
                next.add(key)
            } else {
                next.delete(key)
            }

            return next
        })
    }

    useEffect((): void => {
        if (!isConcreteTelSysOrPartitionFilterValue(listTelSysId)) {
            setCurrentTelSys(null)
            setLicenceSummaryForOptions(undefined)
            return
        }

        getTelephonyServer(
            listTelSysId,
            HISTORIC_ONLY,
            INTEGRATION_MODE,
            INTEGRATION_MODE_OPTIONS,
            INTEGRATION_TYPE_FIELD
        )
            .then((telSys: Nullable<IndividualTelItem>): void => {
                if (isMounted.current) {
                    setCurrentTelSys(normaliseTelSysIntegrationType(telSys))
                }
            })
            .catch((): void => {
                if (isMounted.current) {
                    setCurrentTelSys(null)
                }
            })

        if (isStringNotEmpty(listCmeId)) {
            getCME(listCmeId)
                .then((cme: Nullable<CMEResponse>): void => {
                    if (isMounted.current) setCmeCapabilities(cme?.Capabilities ?? null)
                })
                .catch((): void => {
                    if (isMounted.current) setCmeCapabilities(null)
                })
        } else {
            setCmeCapabilities(undefined)
        }

        getTelSysLicences(listTelSysId)
            .then((licenceSummary: Nullable<LicenceResponseMapped>): void => {
                if (isMounted.current) {
                    setLicenceSummaryForOptions(licenceSummary)
                }
            })
            .catch((): void => {
                if (isMounted.current) {
                    setLicenceSummaryForOptions(undefined)
                }
            })
    }, [listTelSysId, listCmeId, isMounted])

    const mapItemToRow = useCallback(
        (item: LicenceManagementListItem, key: Key): LicenceManagementListRow => {
            const entryType: LicenceManagementEntryTypeValue = item.EntryType ?? LicenceManagementEntryType.DEVICE
            const userLicenceValue: string = getUserLicenceValue(item, entryType, listTelSysType)

            const userLicence: string = getSharedLicenceDisplayValue(
                userLicenceValue,
                language,
                USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
                listTelSysType,
                listIntegrationType
            )
            const cxiLicence: string = getSharedLicenceDisplayValue(
                item.CXILicence,
                language,
                CXI_LICENCE_LANGUAGE_KEY_BY_VALUE,
                listTelSysType
            )
            const cxsLicence: string = getSharedLicenceDisplayValue(
                item.CXSLicence,
                language,
                CXS_LICENCE_LANGUAGE_KEY_BY_VALUE
            )
            const cxrLicence: string = getSharedLicenceDisplayValue(
                item.CXRLicence,
                language,
                CXR_LICENCE_LANGUAGE_KEY_BY_VALUE
            )
            const cxiDataEntitlement: boolean =
                (item.CXIDataEntitlement ?? EMPTY_STRING).toUpperCase() === CxiDataEntitlement.CXI_DATA
            const licenceValues: LicenceDraftValues = {
                userLicence: userLicenceValue,
                cxiLicence: (IsStringEmpty(item.CXILicence)
                    ? CxIntegrationLicence.NONE
                    : item.CXILicence) as CxIntegrationLicence,
                cxiDataEntitlement,
                cxsLicence: (IsStringEmpty(item.CXSLicence)
                    ? CXScoreLicences.NONE
                    : item.CXSLicence) as CXScoreLicences,
                cxrLicence: (IsStringEmpty(item.CXRLicence)
                    ? CXRecordLicences.NONE
                    : item.CXRLicence) as CXRecordLicences,
                // The record flag lives on the device. The list API only sets the device flag
                // (CXREnabled) for a user row and leaves the user-scope CXRecordEnabled false, so combine
                // both to reflect the persisted state for user and licence-less rows alike.
                cxrRecordEnabled: (item.CXRecordEnabled ?? false) || (item.CXREnabled ?? false),
                cxsScoreEnabled: item.CXScoreEnabled ?? false
            }

            return {
                key,
                Description: item.Description ?? EMPTY_STRING,
                EntryType: entryType,
                EntryTypeDisplay:
                    entryType === LicenceManagementEntryType.USER
                        ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Type_User)
                        : language.get(LicenceManagementPageLanguage.LicenceManagementPage_Type_Device),
                TelSysID: item.TelSysID ?? 0,
                TelSysDescription: item.TelSysDescription ?? EMPTY_STRING,
                PartIDInTelSys: item.PartIDInTelSys ?? ALL_VALUE,
                PartitionDescription: item.PartitionDescription ?? EMPTY_STRING,
                DeviceNumber: item.DeviceNumber ?? EMPTY_STRING,
                TelSysUsername: item.TelSysUsername ?? EMPTY_STRING,
                HasDevice: item.HasDevice ?? false,
                Username: item.Username ?? EMPTY_STRING,
                FullName: item.FullName ?? EMPTY_STRING,
                EmailAddr: item.EmailAddr ?? EMPTY_STRING,
                AgentNumber: item.AgentNumber ?? EMPTY_STRING,
                WelcomeEmail: item.WelcomeEmail ?? true,
                UserLicence: userLicence,
                UserLicenceValue: licenceValues.userLicence,
                CXILicence: cxiLicence,
                CXILicenceValue: licenceValues.cxiLicence,
                CXIDataEnabled: licenceValues.cxiDataEntitlement,
                CXSLicence: cxsLicence,
                CXSLicenceValue: licenceValues.cxsLicence,
                CXRLicence: cxrLicence,
                CXRLicenceValue: licenceValues.cxrLicence,
                CXRecordEnabled: licenceValues.cxrRecordEnabled,
                CXScoreEnabled: item.CXScoreEnabled ?? false,
                CXREnabled: item.CXREnabled ?? false,
                HasAgent: item.HasAgent ?? false,
                Licences: buildLicenceDisplayValue(
                    licenceValues,
                    language,
                    isCxiDataEnabled,
                    listTelSysType,
                    listIntegrationType
                ),
                Status: item.Status ?? EMPTY_STRING,
                PendingDeletion: item.PendingDeletion ?? false
            }
        },
        [language, listTelSysType, listIntegrationType, isCxiDataEnabled]
    )

    /** Stores the raw licence summary; display labels are derived at render so they always
     * reflect the loaded telsys details (integration type) even when those arrive later. */
    const applySummaryFromResponse = useCallback((response: Nullable<LicenceManagementListResponse>): void => {
        setRawSummary(response?.Data?.Summary ?? EMPTY_SUMMARY)
    }, [])

    // Display labels derive from the raw summary at render so they always reflect the loaded
    // telsys details (integration type drives the Teams Business licence names).
    const summary: LicenceManagementSummary = useMemo(
        (): LicenceManagementSummary => ({
            ...rawSummary,
            CXALicences: mapCxLicenceSummaryDisplayValues(
                rawSummary.CXALicences,
                language,
                USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
                listTelSysType,
                listIntegrationType
            ),
            CXSLicences: mapCxLicenceSummaryDisplayValues(
                rawSummary.CXSLicences,
                language,
                CXS_LICENCE_LANGUAGE_KEY_BY_VALUE
            ),
            CXILicences: mapCxLicenceSummaryDisplayValues(
                rawSummary.CXILicences,
                language,
                CXI_LICENCE_LANGUAGE_KEY_BY_VALUE,
                listTelSysType
            ),
            CXRLicences: mapCxLicenceSummaryDisplayValues(
                rawSummary.CXRLicences,
                language,
                CXR_LICENCE_LANGUAGE_KEY_BY_VALUE
            )
        }),
        [rawSummary, language, listTelSysType, listIntegrationType]
    )

    const refreshRow = useCallback(
        (row: LicenceManagementListRow): void => {
            const [currentPage, rowsPerPage]: PaginationInfo = pagination
            const pageStartIndex: number = currentPage * rowsPerPage

            getLicenceManagementList(
                listTelSysId,
                listPartitionId,
                listCmeId,
                listIncludeRecursive,
                pageStartIndex,
                rowsPerPage,
                sort,
                listSearch
            )
                .then((response: Nullable<LicenceManagementListResponse>): void => {
                    if (!isMounted.current) return

                    const mappedRows: readonly LicenceManagementListRow[] =
                        response?.Data?.Items?.map(
                            (item: LicenceManagementListItem, index: number): LicenceManagementListRow =>
                                mapItemToRow(
                                    item,
                                    buildAdminListRowKey(
                                        [
                                            listCmeId,
                                            item.TelSysID,
                                            item.PartIDInTelSys,
                                            item.EntryType ?? LicenceManagementEntryType.DEVICE,
                                            item.DeviceNumber,
                                            item.Username,
                                            item.Description
                                        ],
                                        pageStartIndex,
                                        index
                                    )
                                )
                        ) ?? []

                    setRows(mappedRows)
                    setPendingRowDraftByKey(
                        (prev: ReadonlyMap<Key, PendingRowDraft>): ReadonlyMap<Key, PendingRowDraft> =>
                            mergePrefilledEmailDrafts(
                                prev,
                                mappedRows,
                                language,
                                isCxiDataEnabled,
                                listTelSysType,
                                listIntegrationType
                            )
                    )
                    // Licence assignments change the summary too, so the tiles refresh with the row.
                    applySummaryFromResponse(response)
                    getTelSysLicences(listTelSysId)
                        .then((licenceSummary: Nullable<LicenceResponseMapped>): void => {
                            if (isMounted.current) setLicenceSummaryForOptions(licenceSummary)
                        })
                        .catch((): void => {
                            // Cap counts stay as-is on refresh failure.
                        })
                })
                .catch((): void => {
                    // Table stays as-is on refresh failure.
                })
                .finally((): void => {
                    setRowLoading(row.key, false)
                })
        },
        [
            listCmeId,
            listTelSysId,
            listPartitionId,
            listIncludeRecursive,
            listSearch,
            pagination,
            sort,
            language,
            listTelSysType,
            mapItemToRow,
            applySummaryFromResponse,
            isMounted
        ]
    )

    const createUserFromEmail = useCallback(
        (
            row: LicenceManagementListRow,
            email: string,
            licenceValues?: LicenceDraftValues,
            welcomeEmail: boolean = DEFAULT_WELCOME_EMAIL
        ): void => {
            // No existing user — derive username from device the same way UBE does:
            // TelSysUsername is telsys-type-specific (IDOnTelSys for Broadworks,
            // TelSysUserID for Teams, PrimaryEmail for Webex). When empty,
            // fall back to using the email as the username.
            const derivedUsername: string = row.TelSysUsername
            const useEmailAsUsername: boolean = IsStringEmpty(derivedUsername)

            const clearPending = (): void => {
                setPendingRowDraftByKey(
                    (prev: ReadonlyMap<Key, PendingRowDraft>): ReadonlyMap<Key, PendingRowDraft> => {
                        const next: Map<Key, PendingRowDraft> = new Map(prev)

                        next.delete(row.key)

                        return next
                    }
                )
            }

            setLoadingRowKeys((prev: ReadonlySet<Key>): ReadonlySet<Key> => new Set([...prev, row.key]))

            getCMEIDFromTelSys(row.TelSysID)
                .then((cmeID: string | undefined): string => (isVarNotEmpty(cmeID) ? cmeID : DEFAULT_CME_ID))
                .catch((): string => DEFAULT_CME_ID)
                .then(
                    (resolvedCmeID: string): Promise<void> =>
                        getSession().then(async (session: JSONResponse<SessionAPIResponse>): Promise<void> => {
                            const partitionID: number =
                                typeof row.PartIDInTelSys === 'number'
                                    ? row.PartIDInTelSys
                                    : parseInt(row.PartIDInTelSys, 10)
                            const userType: string = getUserTypeForLicenceValue(
                                licenceValues?.userLicence ?? ReportingAccessLevels.ESSENTIALS
                            )
                            // Agent reporting tiers (Agent and Manager) must hold the ACD agent as
                            // their endpoint, not the device. HasAgent means the row resolves to a
                            // matching agent (the matched agent shares the device's number), so the
                            // mapper emits the agent endpoint from the device number. Without a
                            // resolvable agent, keep the device.
                            const isAgentTier: boolean =
                                (userType === UserTypes.CALL_CENTRE_AGENT ||
                                    userType === UserTypes.CALL_CENTRE_SUPERVISOR) &&
                                row.HasAgent

                            const user: AdminUserBody = await userFormStateMapper(
                                userDefaults as unknown as UserDefaults,
                                {
                                    email,
                                    name: row.Description,
                                    userType,
                                    welcomeEmail,
                                    useEmailAsUsername,
                                    cxiLicence: licenceValues?.cxiLicence ?? CxIntegrationLicence.NONE,
                                    cxiDataEntitlement: licenceValues?.cxiDataEntitlement ?? false
                                },
                                resolvedCmeID,
                                row.TelSysID,
                                partitionID,
                                session?.Nonce,
                                {
                                    TelSysUsername: derivedUsername,
                                    Description: row.Description,
                                    DeviceNumber: row.DeviceNumber,
                                    DeviceTypeID: 0
                                },
                                isAgentTier,
                                undefined,
                                undefined,
                                licenceValues?.cxsLicence,
                                licenceValues?.cxsScoreEnabled ?? row.CXScoreEnabled ?? false
                            )

                            // A staged CXS Agent subscription needs the agent endpoint even when the
                            // CXA tier does not (CXA None); the mapper only emits one for the agent
                            // tiers, so it is corrected here (APD-13455).
                            // Only when the row has an agent to point at (APD-13764)
                            const needsAgentEndpoint: boolean =
                                !isAgentTier && requiresAgentEndpointForCxs(licenceValues?.cxsLicence, row.AgentNumber)

                            // The monitoring flags and CXR licence travel with the user create —
                            // the device API is only for rows without a user (APD-12994). Defaults
                            // come from the row's device state so existing flags are not cleared.
                            // Business Real-Time users must be scoped to their own device, not "All".
                            createUser({
                                ...user,
                                ...(needsAgentEndpoint
                                    ? { Endpoint: buildAgentEndpointForRow(row, user.Endpoint) }
                                    : {}),
                                Scope: {
                                    ...user.Scope,
                                    ...(requiresBusinessDeviceScope(userType, listIntegrationType)
                                        ? { DeviceNumber: row.DeviceNumber }
                                        : {}),
                                    CXRLicenseType: licenceValues?.cxrLicence ?? CXRecordLicences.NONE,
                                    CXRecordEnabled: licenceValues?.cxrRecordEnabled ?? row.CXREnabled ?? false
                                }
                            })
                                .then((): void => {
                                    clearPending()
                                    refreshRow(row)
                                })
                                .catch((err: Error): void => {
                                    // A partial result means the user was created but the welcome
                                    // e-mail dispatch was busy; show the warning but still refresh the row.
                                    if (err instanceof RESTAPIError && err.resultType === ResultType.Partial) {
                                        displayError(err)
                                        setLoadingRowKeys(
                                            (prev: ReadonlySet<Key>): ReadonlySet<Key> =>
                                                new Set([...prev].filter((k: Key): boolean => k !== row.key))
                                        )
                                        clearPending()
                                        refreshRow(row)

                                        return
                                    }

                                    setLoadingRowKeys(
                                        (prev: ReadonlySet<Key>): ReadonlySet<Key> =>
                                            new Set([...prev].filter((k: Key): boolean => k !== row.key))
                                    )
                                    displayError(err)
                                    clearPending()
                                })
                        })
                )
                .catch((err: Error): void => {
                    setLoadingRowKeys(
                        (prev: ReadonlySet<Key>): ReadonlySet<Key> =>
                            new Set([...prev].filter((k: Key): boolean => k !== row.key))
                    )
                    displayError(err)
                    clearPending()
                })
        },
        // listTelSysType / listIntegrationType drive the Business device-scope decision, so the
        // callback must recapture them rather than close over their initial (undefined) values.
        [refreshRow, listTelSysType, listIntegrationType]
    )

    const localeText: AdminFiltersLocaleText = useMemo(
        (): AdminFiltersLocaleText => ({
            cmeLabel: language.get(String(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL)),
            telephonyServerLabel: language.get(
                String(GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL)
            ),
            partitionLabel: language.get(String(GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL)),
            includeChildCmeLabel: language.get(
                String(GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL)
            ),
            searchLabel: language.get(String(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL))
        }),
        [language]
    )

    const fetchList = useCallback((): void => {
        if (IsVarEmpty(listCmeId)) return

        setLoading((currentLoading: number): number => currentLoading + 1)

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setRows([])
                setRawSummary(EMPTY_SUMMARY)
                setTotalCount(0)
            }

            setLoading((currentLoading: number): number => currentLoading - 1)
            return
        }

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage

        getLicenceManagementList(
            listTelSysId,
            listPartitionId,
            listCmeId,
            listIncludeRecursive,
            pageStartIndex,
            rowsPerPage,
            sort,
            listSearch
        )
            .then((response: Nullable<LicenceManagementListResponse>): void => {
                if (!isMounted.current) {
                    return
                }

                const mappedRows: readonly LicenceManagementListRow[] =
                    response?.Data?.Items?.map(
                        (item: LicenceManagementListItem, index: number): LicenceManagementListRow =>
                            mapItemToRow(
                                item,
                                buildAdminListRowKey(
                                    [
                                        listCmeId,
                                        item.TelSysID,
                                        item.PartIDInTelSys,
                                        item.EntryType ?? LicenceManagementEntryType.DEVICE,
                                        item.DeviceNumber,
                                        item.Username,
                                        item.Description
                                    ],
                                    pageStartIndex,
                                    index
                                )
                            )
                    ) ?? []

                setRows(mappedRows)
                setPendingRowDraftByKey(
                    buildPrefilledEmailDrafts(
                        mappedRows,
                        language,
                        isCxiDataEnabled,
                        listTelSysType,
                        listIntegrationType
                    )
                )
                hasLoadedOnce.current = true
                applySummaryFromResponse(response)
                setTotalCount(response?.Data?.Total ?? 0)
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setLoading((currentLoading: number): number => currentLoading - 1)
                }
            })
    }, [
        listCmeId,
        listIncludeRecursive,
        listSearch,
        listTelSysId,
        listPartitionId,
        listNoTelephonyServer,
        listTelSysType,
        listIntegrationType,
        pagination,
        sort,
        language,
        mapItemToRow,
        isCxiDataEnabled
    ])

    // Always points to the latest fetchList so effects below can call it without
    // listing fetchList itself as a dep (which would re-fire on every lang/mapItemToRow change).
    const fetchListRef = useRef<() => void>(fetchList)

    useEffect((): void => {
        fetchListRef.current = fetchList
    }, [fetchList])

    // Coalesces same-render effect fires into a single fetch call.
    // A 0 ms timeout lets all synchronous effects in the render run first (e.g. both
    // the filter-values effect and the pagination effect after a filter change), so only
    // one actual request is issued.
    //
    // loadingPreIncrementCountRef tracks how many times the onChange handler has pre-incremented
    // `loading` (to bridge the gap between filtersReady→true and the fetch actually starting).
    // The timer callback cancels those pre-increments before fetchList adds its own +1, so
    // the counter never goes negative even if onChange fires several times before the timer fires.
    const pendingFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const loadingPreIncrementCountRef = useRef<number>(0)

    const scheduleFetch = useCallback((): void => {
        if (pendingFetchTimerRef.current !== null) {
            clearTimeout(pendingFetchTimerRef.current)
        }

        pendingFetchTimerRef.current = setTimeout((): void => {
            pendingFetchTimerRef.current = null

            const preIncrements: number = loadingPreIncrementCountRef.current
            loadingPreIncrementCountRef.current = 0

            if (preIncrements > 0) {
                // Undo the onChange pre-increments; fetchList will add its own +1.
                setLoading((n: number): number => n - preIncrements)
            }

            fetchListRef.current()
        }, 0)
    }, [setLoading])

    // Fires exactly once: when filtersReady first transitions false → true.
    // Subsequent filter/pagination/sort changes are handled by dedicated effects below.
    useEffect((): void => {
        if (!filtersReady) return

        scheduleFetch()
    }, [filtersReady, scheduleFetch])

    // Re-fetch when filter-derived values change after the initial load.
    // language, sort, and pagination are intentionally excluded — they change independently
    // and have their own re-fetch triggers below.
    useEffect((): void => {
        if (!hasLoadedOnce.current) return

        scheduleFetch()
    }, [
        listCmeId,
        listIncludeRecursive,
        listSearch,
        listTelSysId,
        listPartitionId,
        listNoTelephonyServer,
        listTelSysType,
        scheduleFetch
    ])

    // Re-fetch when pagination or sort changes after the initial load.
    useEffect((): void => {
        if (!hasLoadedOnce.current) return

        scheduleFetch()
    }, [pagination, scheduleFetch])

    useEffect((): void => {
        if (!hasLoadedOnce.current) return

        scheduleFetch()
    }, [sort, scheduleFetch])

    const updatePendingLicenceValues = useCallback(
        (row: LicenceManagementListRow, licenceValues: LicenceDraftValues): void => {
            const originalValues: LicenceDraftValues = seedRowLicenceValues(row, deviceMonitoringByKey.get(row.key))
            const hasLicenceChanges: boolean =
                licenceValues.userLicence !== originalValues.userLicence ||
                licenceValues.cxiLicence !== originalValues.cxiLicence ||
                licenceValues.cxiDataEntitlement !== originalValues.cxiDataEntitlement ||
                licenceValues.cxsLicence !== originalValues.cxsLicence ||
                licenceValues.cxrLicence !== originalValues.cxrLicence ||
                licenceValues.cxrRecordEnabled !== originalValues.cxrRecordEnabled ||
                licenceValues.cxsScoreEnabled !== originalValues.cxsScoreEnabled
            const licences: string = buildLicenceDisplayValue(
                licenceValues,
                language,
                isCxiDataEnabled,
                listTelSysType,
                listIntegrationType
            )

            setPendingRowDraftByKey((prev: ReadonlyMap<Key, PendingRowDraft>): ReadonlyMap<Key, PendingRowDraft> => {
                const next: Map<Key, PendingRowDraft> = new Map(prev)
                const currentDraft: PendingRowDraft | undefined = next.get(row.key)

                // Keep the draft alive when the licences changed, or when a pending e-mail is staged /
                // prefilled on the row (e.g. the load-time prefill) so the create-from-e-mail intent
                // survives. Either way store the NEW licence values/display: copying the previous
                // draft's values would discard a revert back to the original licence — the popup toggle
                // would appear stuck "on" and a revert to e.g. Monitoring Deactivated would be lost
                // (APD-13208, APD-13313).
                if (hasLicenceChanges || currentDraft?.email !== undefined) {
                    next.set(row.key, {
                        ...currentDraft,
                        licences,
                        licenceValues
                    })
                } else {
                    next.delete(row.key)
                }

                return next
            })
        },
        [language, listTelSysType, listIntegrationType, deviceMonitoringByKey, isCxiDataEnabled]
    )

    /**
     * Builds the tier-default body for a row, used to re-derive the fields a changed reporting level
     * dictates: scope shape, mobile reporting, default report collection and report permissions.
     *
     * <p>Mirrors the AddUser/UBE pattern, so a new reporting level resets the feature permissions it
     * does not allow. It is only consulted on a tier change — see {@link buildLicenceUpdateBody}.</p>
     *
     * @param row Licence management row being submitted.
     * @param licenceValues Staged licence values for the row.
     * @param userType User type resolved from the staged reporting level.
     * @param partitionID Partition the row belongs to.
     * @param isAgentTier Whether the staged tier is an agent tier with an agent available.
     * @returns The tier-default user body.
     */
    const buildTierDefaults = useCallback(
        async (
            row: LicenceManagementListRow,
            licenceValues: LicenceDraftValues,
            userType: string,
            partitionID: number,
            isAgentTier: boolean
        ): Promise<AdminUserBody> => {
            const cmeID: string = await getCMEIDFromTelSys(row.TelSysID)
                .then((resolved: string | undefined): string =>
                    isVarNotEmpty(resolved) ? resolved : DEFAULT_CME_ID
                )
                .catch((): string => DEFAULT_CME_ID)

            return userFormStateMapper(
                userDefaults as unknown as UserDefaults,
                {
                    email: row.EmailAddr,
                    name: row.FullName,
                    userType,
                    welcomeEmail: false,
                    useEmailAsUsername: false,
                    cxiLicence: licenceValues.cxiLicence
                },
                cmeID,
                row.TelSysID,
                partitionID,
                undefined,
                {
                    TelSysUsername: row.TelSysUsername,
                    Description: row.FullName,
                    DeviceNumber: row.DeviceNumber
                } as DevicesResponse,
                isAgentTier,
                row.Username,
                row.Username,
                licenceValues.cxsLicence
            )
        },
        []
    )

    /**
     * Saves any changed device-level CX monitoring flags straight to the device API. Resolves to
     * true when an update was sent. Runs after any user save so the explicit device state is not
     * clobbered by the backend's licence-driven flag sync.
     */
    const applyDeviceMonitoring = useCallback(
        (row: LicenceManagementListRow, licenceValues: LicenceDraftValues): Promise<boolean> => {
            if (!row.HasDevice || IsStringEmpty(row.DeviceNumber)) return Promise.resolve(false)

            const seeded: LicenceDraftValues = seedRowLicenceValues(row, deviceMonitoringByKey.get(row.key))
            const body: DeviceAdminDTO = {}

            if (licenceValues.cxsScoreEnabled !== seeded.cxsScoreEnabled) {
                body.CXScoreEnabled = licenceValues.cxsScoreEnabled
            }

            // Record monitoring only goes device-direct for unlicensed (None) rows; licensed users
            // carry the record flag through the user save (Scope.CXRecordEnabled).
            if (
                licenceValues.cxrLicence === CXRecordLicences.NONE &&
                licenceValues.cxrRecordEnabled !== seeded.cxrRecordEnabled
            ) {
                body.CXREnabled = licenceValues.cxrRecordEnabled
            }

            if (Object.keys(body).length === 0) return Promise.resolve(false)

            return updateDevice(row.TelSysID, row.PartIDInTelSys, row.DeviceNumber, body).then((): boolean => {
                setDeviceMonitoringByKey(
                    (prev: ReadonlyMap<Key, DeviceMonitoringFlags>): ReadonlyMap<Key, DeviceMonitoringFlags> => {
                        const next: Map<Key, DeviceMonitoringFlags> = new Map(prev)
                        const current: Definable<DeviceMonitoringFlags> = next.get(row.key)

                        next.set(row.key, {
                            cxsScoreEnabled: body.CXScoreEnabled ?? current?.cxsScoreEnabled ?? false,
                            cxrEnabled: body.CXREnabled ?? current?.cxrEnabled ?? false
                        })

                        return next
                    }
                )

                return true
            })
        },
        [deviceMonitoringByKey]
    )

    /**
     * Records the saved monitoring flags as the new device baseline after a user-API save —
     * the backend applies Scope.CXScoreEnabled / CXRecordEnabled to the device, so the popup
     * seeding and change detection must treat the saved values as current (APD-12994).
     */
    const setDeviceMonitoringBaseline = useCallback(
        (row: LicenceManagementListRow, licenceValues: LicenceDraftValues): void => {
            setDeviceMonitoringByKey(
                (prev: ReadonlyMap<Key, DeviceMonitoringFlags>): ReadonlyMap<Key, DeviceMonitoringFlags> => {
                    const next: Map<Key, DeviceMonitoringFlags> = new Map(prev)

                    next.set(row.key, {
                        cxsScoreEnabled: licenceValues.cxsScoreEnabled,
                        cxrEnabled: licenceValues.cxrRecordEnabled
                    })

                    return next
                }
            )
        },
        []
    )

    // Seed the popup's monitoring toggles from the device when the licences popup opens.
    useEffect((): void => {
        const row: Nullable<LicenceManagementListRow> = selectedLicenceRow

        if (!row || !row.HasDevice || IsStringEmpty(row.DeviceNumber)) return

        if (deviceMonitoringByKey.has(row.key)) return

        getDevice(row.TelSysID, row.PartIDInTelSys, row.DeviceNumber)
            .then((response: JSONResponse<APIData<DeviceResponse>>): void => {
                const device: Definable<DeviceResponse> = response?.Data

                setDeviceMonitoringByKey(
                    (prev: ReadonlyMap<Key, DeviceMonitoringFlags>): ReadonlyMap<Key, DeviceMonitoringFlags> => {
                        const next: Map<Key, DeviceMonitoringFlags> = new Map(prev)

                        next.set(row.key, {
                            cxsScoreEnabled: device?.CXScoreEnabled ?? false,
                            cxrEnabled: device?.CXREnabled ?? false
                        })

                        return next
                    }
                )
            })
            .catch((): void => {
                // Seeding is best-effort; the toggles fall back to off when the device read fails.
            })
    }, [selectedLicenceRow, deviceMonitoringByKey])

    /**
     * Renders the Licences cell content: licences stacked one per line, collapsing to an
     * "[X Licences]" summary beyond {@link MAX_STACKED_ROW_LICENCES}, or the remove icon when the
     * row holds no licences.
     */
    const renderLicencesCellContent = useCallback(
        (licencesDisplayValue: string): React.ReactNode => {
            if (licencesDisplayValue === EMPTY_STRING) {
                return (
                    <span className='lmp-cell-icon'>
                        <Icon iconName={IconName.REMOVE} />
                    </span>
                )
            }

            const licenceParts: string[] = licencesDisplayValue.split(LICENCE_DISPLAY_SEPARATOR)

            if (licenceParts.length > MAX_STACKED_ROW_LICENCES) {
                return language.getInterpolated(
                    LicenceManagementPageLanguage.LicenceManagementPage_Column_LicencesCount,
                    licenceParts.length
                )
            }

            return licenceParts.map(
                (licence: string): React.ReactNode => (
                    <span key={licence} className='lmp-cell-licence-line'>
                        {licence}
                    </span>
                )
            )
        },
        [language]
    )

    const columns: readonly DataTableColumn<LicenceManagementListRow, keyof LicenceManagementListRow>[] = useMemo(
        (): readonly DataTableColumn<LicenceManagementListRow, keyof LicenceManagementListRow>[] => [
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_DeviceNumber),
                field: 'DeviceNumber',
                key: 'licence-management-page-device-number',
                sortable: true,
                serverSortField: 'DeviceNumber',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode =>
                    row.DeviceNumber !== EMPTY_STRING ? (
                        <span
                            role='button'
                            tabIndex={0}
                            className='lmp-cell-link'
                            onClick={(e: React.MouseEvent): void => {
                                e.stopPropagation()
                                setSelectedDeviceRow(row)
                            }}
                            onKeyDown={(e: React.KeyboardEvent): void => {
                                if (e.key === 'Enter') setSelectedDeviceRow(row)
                            }}
                        >
                            {row.DeviceNumber}
                        </span>
                    ) : (
                        <div className='lmp-cell-icon'>
                            <Icon iconName={IconName.REMOVE} />
                        </div>
                    )
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_Username),
                field: 'Username',
                key: 'licence-management-page-username',
                sortable: true,
                serverSortField: 'Username',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    const pendingRowDraft: PendingRowDraft | undefined = pendingRowDraftByKey.get(row.key)

                    // A real application user is linked: show it as a blue link that opens the user modal.
                    if (isStringNotEmpty(row.Username)) {
                        return (
                            <span
                                role='button'
                                tabIndex={0}
                                className='lmp-cell-link'
                                onClick={(e: React.MouseEvent): void => {
                                    e.stopPropagation()
                                    setSelectedUserRow(row)
                                }}
                                onKeyDown={(e: React.KeyboardEvent): void => {
                                    if (e.key === 'Enter') setSelectedUserRow(row)
                                }}
                            >
                                {row.Username}
                            </span>
                        )
                    }

                    // No user exists yet: show the username that would be used when one is created —
                    // the pending draft (e.g. the load-time e-mail prefill) or the telephony username — in
                    // plain (non-link) styling with an explanatory tooltip (APD-13333).
                    const prospectiveUsername: string = pendingRowDraft?.username ?? row.TelSysUsername

                    if (isStringNotEmpty(prospectiveUsername)) {
                        return (
                            <span
                                className='lmp-cell-username-inactive'
                                title={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Username_NoUserTooltip
                                )}
                            >
                                {prospectiveUsername}
                            </span>
                        )
                    }

                    return (
                        <div className='lmp-cell-icon'>
                            <Icon iconName={IconName.REMOVE} />
                        </div>
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_Email),
                field: 'EmailAddr',
                key: 'licence-management-page-email',
                sortable: true,
                serverSortField: 'EmailAddr',
                hideBelow: 'md',
                align: 'center',
                disableRowSelection: true,
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    return (
                        <EmailEditCell
                            row={row}
                            pendingEmail={pendingRowDraftByKey.get(row.key)?.email}
                            defaultEmail={
                                listTelSysType === TelSysTypes.MS_TEAMS && isStringNotEmpty(row.TelSysUsername)
                                    ? row.TelSysUsername
                                    : undefined
                            }
                            addEmailTooltip={language.get(
                                LicenceManagementPageLanguage.LicenceManagementPage_Email_AddTooltip
                            )}
                            onSaved={(): void => {
                                refreshRow(row)
                            }}
                            onLoadingChange={(isRowLoading: boolean): void => setRowLoading(row.key, isRowLoading)}
                            onPendingChange={(email: string | null): void => {
                                setPendingRowDraftByKey(
                                    (prev: ReadonlyMap<Key, PendingRowDraft>): ReadonlyMap<Key, PendingRowDraft> => {
                                        const next: Map<Key, PendingRowDraft> = new Map(prev)

                                        if (email !== null) {
                                            const pendingUserDraft: PendingRowDraft = getPendingUserDraft(
                                                row,
                                                email,
                                                language,
                                                isCxiDataEnabled,
                                                listTelSysType,
                                                listIntegrationType
                                            )

                                            next.set(row.key, {
                                                ...pendingUserDraft,
                                                // The operator entered this e-mail: mark it as an explicit
                                                // create-user intent so the tick creates a user (APD-13333).
                                                emailStagedByUser: true,
                                                licenceValues:
                                                    next.get(row.key)?.licenceValues ?? pendingUserDraft.licenceValues,
                                                licences: next.get(row.key)?.licences ?? pendingUserDraft.licences,
                                                welcomeEmail: next.get(row.key)?.welcomeEmail
                                            })
                                        } else {
                                            const currentDraft: PendingRowDraft | undefined = next.get(row.key)

                                            if (
                                                currentDraft?.licenceValues !== undefined ||
                                                currentDraft?.welcomeEmail !== undefined
                                            ) {
                                                next.set(row.key, {
                                                    licenceValues: currentDraft.licenceValues,
                                                    licences: currentDraft.licences,
                                                    welcomeEmail: currentDraft.welcomeEmail
                                                })
                                            } else {
                                                next.delete(row.key)
                                            }
                                        }

                                        return next
                                    }
                                )
                            }}
                        />
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_HasDevice),
                field: 'HasDevice',
                key: 'licence-management-page-has-device',
                sortable: true,
                serverSortField: 'HasDevice',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    // Tick: the device is assigned to a user and/or CX-monitored. Dash: a device
                    // exists but is neither. X: the row has no device.
                    const deviceExists: boolean = row.EntryType === LicenceManagementEntryType.DEVICE || row.HasDevice
                    const deviceAssigned: boolean =
                        row.EntryType === LicenceManagementEntryType.DEVICE
                            ? isStringNotEmpty(row.Username)
                            : row.HasDevice
                    const deviceMonitored: boolean = row.CXScoreEnabled || row.CXREnabled

                    let iconName: IconName

                    if (!deviceExists) {
                        iconName = IconName.X
                    } else if (deviceAssigned || deviceMonitored) {
                        iconName = IconName.CHECK
                    } else {
                        iconName = IconName.REMOVE
                    }

                    return (
                        <div className='lmp-cell-icon lmp-cell-icon--device'>
                            <Icon iconName={iconName} />
                        </div>
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_Agent),
                field: 'AgentNumber',
                key: 'licence-management-page-agent',
                sortable: true,
                serverSortField: 'AgentNumber',
                hideBelow: 'md',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    // Tick: an ACD agent is assigned. Dash: an agent exists for the row's device
                    // but is not assigned. X: no agent exists.
                    let iconName: IconName

                    if (isStringNotEmpty(row.AgentNumber)) {
                        iconName = IconName.CHECK
                    } else if (row.HasAgent) {
                        iconName = IconName.REMOVE
                    } else {
                        iconName = IconName.X
                    }

                    return (
                        <div className='lmp-cell-icon lmp-cell-icon--agent'>
                            <Icon iconName={iconName} />
                        </div>
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_WelcomeEmail),
                field: 'WelcomeEmail',
                key: 'licence-management-page-welcome-email',
                sortable: true,
                serverSortField: 'WelcomeEmail',
                hideBelow: 'md',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    if (isStringNotEmpty(row.Username)) {
                        const emailTitle: string = language.getInterpolated(
                            LicenceManagementPageLanguage.LicenceManagementPage_WelcomeEmail_Title,
                            row.EmailAddr
                        )

                        return (
                            <div
                                className='lmp-cell-action'
                                onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                            >
                                <IconButton
                                    type={IconButtonType.BUTTON}
                                    iconName={IconName.EMAIL}
                                    title={emailTitle}
                                    onClick={(): void => {
                                        displayPromptPromise(
                                            language.getInterpolated(
                                                LicenceManagementPageLanguage.LicenceManagementPage_WelcomeEmail_Confirm,
                                                row.EmailAddr
                                            )
                                        )
                                            .then((): void => {
                                                sendWelcomeEmailForUser(row.Username)
                                                    .then((): void => {
                                                        displaySuccessMessage(
                                                            language.getInterpolated(
                                                                LicenceManagementPageLanguage.LicenceManagementPage_WelcomeEmail_Success,
                                                                row.EmailAddr
                                                            )
                                                        )
                                                    })
                                                    .catch((): void => {
                                                        displayErrorMessage(
                                                            language.get(
                                                                LicenceManagementPageLanguage.LicenceManagementPage_WelcomeEmail_Fail
                                                            )
                                                        )
                                                    })
                                            })
                                            .catch((): void => {
                                                /* Prompt was dismissed, do nothing */
                                            })
                                    }}
                                />
                            </div>
                        )
                    }

                    const welcomeEmailDraft: PendingRowDraft | undefined = pendingRowDraftByKey.get(row.key)
                    const isWelcomeEmailChecked: boolean = welcomeEmailDraft?.welcomeEmail ?? DEFAULT_WELCOME_EMAIL

                    return (
                        <div className='lmp-cell-action' onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
                            <RouterCheckbox
                                isChecked={isWelcomeEmailChecked}
                                onChange={(checked: boolean): void => {
                                    setPendingRowDraftByKey(
                                        (
                                            prev: ReadonlyMap<Key, PendingRowDraft>
                                        ): ReadonlyMap<Key, PendingRowDraft> => {
                                            const next: Map<Key, PendingRowDraft> = new Map(prev)
                                            const currentDraft: PendingRowDraft | undefined = next.get(row.key)
                                            // Toggling the flag back to its default on a row that has no
                                            // other staged change clears the draft so no phantom save tick
                                            // is left behind.
                                            if (
                                                checked === DEFAULT_WELCOME_EMAIL &&
                                                currentDraft?.email === undefined &&
                                                currentDraft?.licenceValues === undefined
                                            ) {
                                                next.delete(row.key)

                                                return next
                                            }

                                            next.set(row.key, { ...currentDraft, welcomeEmail: checked })

                                            return next
                                        }
                                    )
                                }}
                            />
                        </div>
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_Licences),
                field: 'Licences',
                key: 'licence-management-page-licences',
                sortable: true,
                serverSortField: 'UserLicence',
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    const pendingRowDraft: PendingRowDraft | undefined = pendingRowDraftByKey.get(row.key)
                    // Derived at render (not from row.Licences) so the labels always reflect the
                    // loaded telsys details — Teams Business names match the popup's options.
                    const licencesDisplayValue: string =
                        pendingRowDraft?.licences ??
                        (row.Licences === EMPTY_STRING
                            ? EMPTY_STRING
                            : buildLicenceDisplayValue(
                                  getRowLicenceValues(row),
                                  language,
                                  isCxiDataEnabled,
                                  listTelSysType,
                                  listIntegrationType
                              ))

                    if (!licenceSectionsAvailable) {
                        return (
                            <span className='lmp-cell-link lmp-cell-link--disabled' aria-disabled='true'>
                                {renderLicencesCellContent(licencesDisplayValue)}
                            </span>
                        )
                    }

                    return (
                        <span
                            role='button'
                            tabIndex={0}
                            className={`lmp-cell-link ${pendingRowDraft?.licenceValues ? 'lmp-cell-pending' : ''}`}
                            onClick={(e: React.MouseEvent<HTMLSpanElement>): void => {
                                e.stopPropagation()
                                setLicencePopoverAnchor(e.currentTarget)
                                setSelectedLicenceRow(row)
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>): void => {
                                if (e.key !== 'Enter') return

                                setLicencePopoverAnchor(e.currentTarget)
                                setSelectedLicenceRow(row)
                            }}
                        >
                            {renderLicencesCellContent(licencesDisplayValue)}
                        </span>
                    )
                }
            },
            {
                headerName: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Column_Actions),
                field: 'Actions',
                key: 'licence-management-page-actions',
                sortable: false,
                align: 'center',
                renderCell: (row: LicenceManagementListRow): React.ReactNode => {
                    if (loadingRowKeys.has(row.key)) return <Skeleton />

                    // Soft-deleted (pending deletion) rows are disabled everywhere else and only
                    // offer a restore action, matching the legacy per-extension "Users" modal.
                    if (row.PendingDeletion && isStringNotEmpty(row.Username)) {
                        return (
                            <RestoreUserCell
                                row={row}
                                onRestored={(): void => {
                                    refreshRow(row)
                                }}
                                onLoadingChange={(isRowLoading: boolean): void => setRowLoading(row.key, isRowLoading)}
                            />
                        )
                    }

                    const pendingRowDraft: PendingRowDraft | undefined = pendingRowDraftByKey.get(row.key)

                    if (pendingRowDraft !== undefined) {
                        const draftValues: Definable<LicenceDraftValues> = pendingRowDraft.licenceValues
                        const seededValues: LicenceDraftValues = seedRowLicenceValues(
                            row,
                            deviceMonitoringByKey.get(row.key)
                        )
                        // Monitoring-only provisioning (APD-12965): a changed Score toggle, or a
                        // changed Record toggle on a "None" licence, saves straight to the device
                        // rather than through user provisioning.
                        const hasDeviceMonitoringChange: boolean =
                            row.HasDevice &&
                            draftValues !== undefined &&
                            (draftValues.cxsScoreEnabled !== seededValues.cxsScoreEnabled ||
                                (draftValues.cxrLicence === CXRecordLicences.NONE &&
                                    draftValues.cxrRecordEnabled !== seededValues.cxrRecordEnabled))
                        const hasUserAffectingChange: boolean =
                            draftValues !== undefined &&
                            (draftValues.userLicence !== seededValues.userLicence ||
                                draftValues.cxiLicence !== seededValues.cxiLicence ||
                                draftValues.cxiDataEntitlement !== seededValues.cxiDataEntitlement ||
                                draftValues.cxsLicence !== seededValues.cxsLicence ||
                                draftValues.cxrLicence !== seededValues.cxrLicence ||
                                (draftValues.cxrLicence !== CXRecordLicences.NONE &&
                                    draftValues.cxrRecordEnabled !== seededValues.cxrRecordEnabled))
                        // Whether a licence (CX Analytics / CX Score / CX Record) is selected on the draft.
                        const hasLicenceSelected: boolean =
                            draftValues !== undefined &&
                            (draftValues.userLicence !== ReportingAccessLevels.NONE ||
                                draftValues.cxsLicence !== CXScoreLicences.NONE ||
                                draftValues.cxrLicence !== CXRecordLicences.NONE)
                        const clearRowDraft = (): void => {
                            setPendingRowDraftByKey(
                                (prev: ReadonlyMap<Key, PendingRowDraft>): ReadonlyMap<Key, PendingRowDraft> => {
                                    const next: Map<Key, PendingRowDraft> = new Map(prev)

                                    next.delete(row.key)

                                    return next
                                }
                            )
                        }
                        // Tick-enabled rules (APD-13333):
                        //  - Existing user: enabled for any staged licence/monitoring change.
                        //  - No user, monitoring-only change: enabled (saves to the device, no e-mail needed).
                        //  - No user, creating one (email staged or a licence selected): enabled only with a
                        //    valid e-mail.
                        //  - No user, bare prefill with no change: disabled (nothing to save — never
                        //    create a licence-less user just from the prefilled e-mail).
                        const hasValidPendingEmail: boolean =
                            isStringNotEmpty(pendingRowDraft.email) &&
                            isEmailAddressValid(pendingRowDraft.email ?? EMPTY_STRING)
                        const isMonitoringOnlyChange: boolean =
                            hasDeviceMonitoringChange && !hasLicenceSelected && !hasUserAffectingChange
                        const wantsToCreateUser: boolean =
                            pendingRowDraft.emailStagedByUser === true || hasLicenceSelected || hasUserAffectingChange
                        const isTickEnabled: boolean = isStringNotEmpty(row.Username)
                            ? hasLicenceSelected || hasUserAffectingChange || hasDeviceMonitoringChange
                            : isMonitoringOnlyChange || (wantsToCreateUser && hasValidPendingEmail)

                        return (
                            <div
                                className='lmp-cell-action'
                                onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                            >
                                <IconButton
                                    type={IconButtonType.BUTTON}
                                    iconName={IconName.CHECK}
                                    isEnabled={isTickEnabled}
                                    title={language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tick_Save)}
                                    disabledSubTitle={language.get(
                                        pendingRowDraft.email !== undefined
                                            ? LicenceManagementPageLanguage.LicenceManagementPage_Tick_InvalidEmail
                                            : LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_EmailRequired
                                    )}
                                    onClick={(e?: React.MouseEvent): void => {
                                        e?.stopPropagation()

                                        // A monitoring toggle (CX Score / device-level CX Record) with no
                                        // licence selected and none changing applies to the device alone
                                        // and must never create or update a user — even when an email is
                                        // prefilled for the row or a username is shown for a device
                                        // with no application user (APD-13333). The explicit email-entry
                                        // create flow (no monitoring change) is unaffected.
                                        if (
                                            hasDeviceMonitoringChange &&
                                            draftValues !== undefined &&
                                            !hasLicenceSelected &&
                                            !hasUserAffectingChange
                                        ) {
                                            applyDeviceMonitoring(row, draftValues)
                                                .then((): void => {
                                                    clearRowDraft()
                                                    refreshRow(row)
                                                })
                                                .catch(displayError)

                                            return
                                        }

                                        // Create a user when the operator explicitly staged an email, selected
                                        // a licence, or toggled a user-level entitlement (CXI User / CXI Data)
                                        // — using the entered or prefilled e-mail. A bare prefill with no
                                        // change is still excluded: hasUserAffectingChange is false there, so the
                                        // tick stays disabled and this branch is never reached (APD-13333).
                                        if (
                                            IsStringEmpty(row.Username) &&
                                            pendingRowDraft.email !== undefined &&
                                            (pendingRowDraft.emailStagedByUser === true ||
                                                hasLicenceSelected ||
                                                hasUserAffectingChange)
                                        ) {
                                            // Create the user from the entered or prefilled email.
                                            // Monitoring flags travel inside the user create body — no
                                            // separate device API call (APD-12994).
                                            createUserFromEmail(
                                                row,
                                                pendingRowDraft.email,
                                                pendingRowDraft.licenceValues,
                                                pendingRowDraft.welcomeEmail ?? DEFAULT_WELCOME_EMAIL
                                            )

                                            return
                                        }

                                        if (draftValues !== undefined) {
                                            if (IsStringEmpty(row.Username)) {
                                                // A licence is selected but there is no user yet:
                                                // creating one requires an email address.
                                                displayErrorMessage(
                                                    language.get(
                                                        LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_EmailRequired
                                                    )
                                                )

                                                return
                                            }

                                            const partitionID: number =
                                                typeof row.PartIDInTelSys === 'number'
                                                    ? row.PartIDInTelSys
                                                    : parseInt(row.PartIDInTelSys, 10)
                                            const userType: string = getUserTypeForLicenceValue(draftValues.userLicence)
                                            const isAgentTier: boolean =
                                                userType === UserTypes.CALL_CENTRE_AGENT &&
                                                isStringNotEmpty(row.AgentNumber)

                                            // Tier-derived fields are only re-derived when the
                                            // reporting level actually changed; a monitoring or CXI
                                            // toggle must leave the rest of the account alone
                                            // (APD-13568).
                                            const hasTierChange: boolean =
                                                draftValues.userLicence !== seededValues.userLicence

                                            getUser(row.Username)
                                                .then(
                                                    async (
                                                        response: JSONResponse<APIData<ApplicationUser>>
                                                    ): Promise<unknown> => {
                                                        const existing: Definable<ApplicationUser> =
                                                            response?.Data

                                                        if (!existing) {
                                                            throw new Error(
                                                                language.get(
                                                                    LicenceManagementPageLanguage.LicenceManagementPage_Licence_SaveFailed
                                                                )
                                                            )
                                                        }

                                                        const tierDefaults: Definable<AdminUserBody> =
                                                            hasTierChange
                                                                ? await buildTierDefaults(
                                                                      row,
                                                                      draftValues,
                                                                      userType,
                                                                      partitionID,
                                                                      isAgentTier
                                                                  )
                                                                : undefined
                                                        // Agent reporting tiers must hold the ACD agent
                                                        // as their endpoint, and so must a staged CXS
                                                        // Agent subscription whatever the CXA tier is
                                                        // (APD-13455), but only when the row has an agent,
                                                        // not invented from its device number (APD-13764).
                                                        // Otherwise the user's own endpoint
                                                        // stands, whether that is a device or an agent.
                                                        const needsAgentEndpoint: boolean =
                                                            isAgentTier ||
                                                            requiresAgentEndpointForCxs(draftValues.cxsLicence, row.AgentNumber)
                                                        const baseEndpoint: AdminUserBody['Endpoint'] =
                                                            needsAgentEndpoint
                                                                ? buildAgentEndpointForRow(row, {
                                                                      TelSysID: row.TelSysID,
                                                                      PartIDInTelSys: partitionID,
                                                                      DeviceNumber: null,
                                                                      AgentNumber: null
                                                                  })
                                                                : existing.Endpoint
                                                        // Turning monitoring on for an agent-only endpoint
                                                        // fills in the agent's device rather than failing,
                                                        // keeping the agent (APD-13568). Skipped where the
                                                        // endpoint is deliberately the agent alone, which
                                                        // CXS Agent requires (APD-13455).
                                                        const endpoint: AdminUserBody['Endpoint'] =
                                                            needsAgentEndpoint
                                                                ? baseEndpoint
                                                                : endpointForTier(
                                                                      userType,
                                                                      baseEndpoint,
                                                                      row,
                                                                      draftValues
                                                                  )
                                                        const body: AdminUserBody = buildLicenceUpdateBody(
                                                            existing,
                                                            draftValues,
                                                            endpoint,
                                                            tierDefaults
                                                        )

                                                        // Rows with a user send the monitoring flags with
                                                        // the licence in a single user API call — the
                                                        // device API is only for rows without a user
                                                        // (APD-12994).
                                                        return updateUser(row.Username, {
                                                            ...body,
                                                            Scope: {
                                                                ...body.Scope,
                                                                // Business Real-Time users must be scoped
                                                                // to their own device, not "All".
                                                                ...(hasTierChange &&
                                                                requiresBusinessDeviceScope(
                                                                    userType,
                                                                    listIntegrationType
                                                                )
                                                                    ? { DeviceNumber: row.DeviceNumber }
                                                                    : {})
                                                            }
                                                        })
                                                    }
                                                )
                                                .then((): void => {
                                                    setDeviceMonitoringBaseline(row, draftValues)
                                                    clearRowDraft()
                                                    refreshRow(row)
                                                })
                                                .catch(displayError)
                                        }
                                    }}
                                />
                            </div>
                        )
                    }

                    if (IsStringEmpty(row.Username)) return null

                    return (
                        <DeleteUserCell
                            row={row}
                            onDeleted={(): void => {
                                refreshRow(row)
                            }}
                            onLoadingChange={(isRowLoading: boolean): void => setRowLoading(row.key, isRowLoading)}
                        />
                    )
                }
            }
        ],
        [
            language,
            displayPromptPromise,
            setSelectedDeviceRow,
            setSelectedUserRow,
            loadingRowKeys,
            refreshRow,
            pendingRowDraftByKey,
            deviceMonitoringByKey,
            applyDeviceMonitoring,
            licenceSectionsAvailable,
            renderLicencesCellContent,
            listTelSysType,
            listIntegrationType,
            createUserFromEmail,
            updatePendingLicenceValues,
            isCxiDataEnabled
        ]
    )

    return (
        <div className='licence-management-page admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_licenceManagement} />
            <AdminFilters
                alignGrid
                optionsToDisplay={{
                    cme: true,
                    telephonyServer: true,
                    partition: true,
                    includeCmeChildren: true,
                    search: true,
                    includeRefresh: true
                }}
                localeText={localeText}
                labelTelSysPartitionAllSentinelAsNone={true}
                telSysLicenceFilter={[ReportingAccessLevels.ENTERPRISE]}
                onRefresh={fetchList}
                onChange={(nextFilters: AdminFilterValues): void => {
                    // Pre-increment loading so the spinner stays visible in the same render
                    // that sets filtersReady=true, before the 0 ms scheduleFetch timer fires.
                    setLoading((n: number): number => n + 1)
                    loadingPreIncrementCountRef.current += 1
                    setFiltersReady(true)
                    setFilters(nextFilters)
                    setPagination(([, currentRowsPerPage]: PaginationInfo): PaginationInfo => [0, currentRowsPerPage])
                }}
            />

            {hasLoadedOnce.current && licenceSectionsAvailable && (
                <Grid container spacing={3} className='lmp-summary-tiles'>
                    {licenceSectionVisibility.cxAnalytics && (
                        <Grid item xs={12} sm={6} lg={summaryTileWidth}>
                            <CxLicenceTile
                                modifierClassName='cx-licence-tile--analytics'
                                iconName={IconName.CX_ANALYTICS}
                                title={language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Analytics)}
                                licencesLabel={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Tile_Licences
                                )}
                                data={summary.CXALicences}
                            />
                        </Grid>
                    )}
                    {licenceSectionVisibility.cxIntegration && (
                        <Grid item xs={12} sm={6} lg={summaryTileWidth}>
                            <CxLicenceTile
                                modifierClassName='cx-licence-tile--integration'
                                iconName={IconName.CRM_CONFIGURATION_SIDEBAR}
                                title={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Tile_Integration
                                )}
                                licencesLabel={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Tile_Licences
                                )}
                                data={summary.CXILicences}
                            />
                        </Grid>
                    )}
                    {licenceSectionVisibility.cxRecord && (
                        <Grid item xs={12} sm={6} lg={summaryTileWidth}>
                            <CxLicenceTile
                                modifierClassName='cx-licence-tile--record'
                                iconName={IconName.RECORDING}
                                title={language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Record)}
                                licencesLabel={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Tile_Licences
                                )}
                                data={summary.CXRLicences}
                            />
                        </Grid>
                    )}
                    {licenceSectionVisibility.cxScore && (
                        <Grid item xs={12} sm={6} lg={summaryTileWidth}>
                            <CxLicenceTile
                                modifierClassName='cx-licence-tile--score'
                                iconName={IconName.CX_SCORE}
                                title={language.get(LicenceManagementPageLanguage.LicenceManagementPage_Tile_Score)}
                                licencesLabel={language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_Tile_Licences
                                )}
                                data={summary.CXSLicences}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            <DataTable
                columns={[...columns]}
                data={[...rows]}
                permissionName={AdminBackendPermissionNames.USER}
                localeText={{
                    noRows: language.get(LicenceManagementPageLanguage.LicenceManagementPage_Empty)
                }}
                getRowClassName={(row: LicenceManagementListRow): string =>
                    row.PendingDeletion ? 'lmp-row-pending-deletion' : EMPTY_STRING
                }
                colIndexToTriggerClick={6}
                isLoading={loading > 0 || !filtersReady}
                toolbarDisabled={true}
                paginationServer={true}
                sortServer={true}
                totalCount={totalCount}
                defaultRowsPerPage={pagination[1]}
                defaultPageNumber={pagination[0]}
                onPageChange={(nextPage: number, rowsPerPage: number): void => {
                    setPagination([nextPage, rowsPerPage])
                }}
                onRowsPerPageChange={(_: number, rowsPerPage: number): void => {
                    setPagination([0, rowsPerPage])
                }}
                onSortChange={setSort}
            />

            {selectedLicenceRow ? (
                <LicenceManagementPopup
                    key={`lmp-licence-popup-${selectedLicenceRow.key}`}
                    row={selectedLicenceRow}
                    anchorEl={licencePopoverAnchor}
                    initialValues={
                        pendingRowDraftByKey.get(selectedLicenceRow.key)?.licenceValues ??
                        seedRowLicenceValues(selectedLicenceRow, deviceMonitoringByKey.get(selectedLicenceRow.key))
                    }
                    language={language}
                    telSysType={listTelSysType}
                    historicOnly={currentTelSys?.HistoricOnly}
                    licenceSummary={licenceSummaryForOptions}
                    businessIntegrationOptions={enabledBusinessOptions}
                    integrationType={currentTelSys?.IntegrationType}
                    integrationMode={currentTelSys?.IntegrationMode}
                    partitionLicences={currentTelSys?.PartitionLicences}
                    callControlOptions={currentTelSys?.CallControlOptions}
                    cxScoreFeatureEnabled={isCxScoreFeatureEnabled}
                    cxIntegrationAvailable={cxIntegrationAvailable}
                    isCxiDataEnabled={isCxiDataEnabled}
                    onChange={(values: LicenceDraftValues): void => {
                        updatePendingLicenceValues(selectedLicenceRow, values)
                    }}
                    onClose={(): void => {
                        setSelectedLicenceRow(null)
                        setLicencePopoverAnchor(null)
                    }}
                />
            ) : null}

            {selectedDeviceRow ? (
                <DeviceConfigurationModal
                    key={`lmp-device-modal-${selectedDeviceRow.TelSysID}-${selectedDeviceRow.PartIDInTelSys}-${selectedDeviceRow.DeviceNumber}`}
                    refreshAction={fetchList}
                    filterTelSysID={selectedDeviceRow.TelSysID}
                    filterPartitionID={Number(selectedDeviceRow.PartIDInTelSys)}
                    telSysType={listTelSysType as TelSysTypes}
                    partitionDisplayLabel={selectedDeviceRow.PartitionDescription}
                    selectedDeviceNumber={
                        selectedDeviceRow.DeviceNumber !== EMPTY_STRING ? selectedDeviceRow.DeviceNumber : undefined
                    }
                    onModalExited={(): void => setSelectedDeviceRow(null)}
                />
            ) : null}

            {selectedUserRow ? (
                <AddUserModal
                    key={`lmp-user-modal-${selectedUserRow.TelSysID}-${selectedUserRow.PartIDInTelSys}-${selectedUserRow.Username}`}
                    filterCMEID={listCmeId}
                    filterTelSysID={selectedUserRow.TelSysID}
                    initialFilters={{
                        cmeID: listCmeId,
                        telSysID: selectedUserRow.TelSysID,
                        partitionID: Number(selectedUserRow.PartIDInTelSys),
                        includeCmeChildren: listIncludeRecursive
                    }}
                    refreshAction={fetchList}
                    selectedUser={selectedUserRow.Username}
                    onModalExited={(): void => setSelectedUserRow(null)}
                />
            ) : null}
        </div>
    )
}
